import React from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, Search, ChevronDown, ChevronRight, Columns as ColumnsIcon, ArrowLeft, X, ChevronsUpDown, Check, Calendar as CalendarIcon, RefreshCw, Download, Printer, Loader2, MoreHorizontal } from 'lucide-react';
import { actionByKey } from '@atlas/shared';
import { api, apiUpload, apiBlob } from '@/lib/api';
import { NzDateTimeInput } from '@/components/shared/nz-datetime-input';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { usePreferences } from '@/hooks/use-preferences';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LookupField, MultiLookupField } from './lookup-field';
import { extractImageUrl, UploadField } from './upload-field';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from '@/components/ui/toast';
import { JobCardChecklist } from './job-card-checklist';
import { OnboardingTasks, TransferTasks } from './onboarding-tasks';
import { AddressAutocomplete } from './address-autocomplete';
import { SwipeToReveal } from './swipe-to-reveal';
import { extractAddressParts } from '@/lib/mapbox';
import { useUserActions } from '@/hooks/use-user-actions';
import { usePermissions } from '@/hooks/use-permissions';
import { useAuthStore } from '@/stores/auth-store';
import { useConfirm } from '@/hooks/use-confirm';
import { cn } from '@/lib/utils';
import { parseNzWallToUtc, startOfNzDay, endOfNzDay, toDateInputValue } from '@/lib/format-date';
import { DetailDialog } from './detail-dialog';
import { RowContextMenu, buildRowContextItems } from './row-context-menu';
import {
  buildExportColumns, downloadBlob, printPdfBlob, fileSlug,
  EXPORT_FILE_FORMATS, type ExportFileFormat,
} from '@/lib/report-export';

// ===== Field config =====
export type FieldType =
  | 'text' | 'textarea' | 'email' | 'number' | 'boolean'
  | 'select' | 'multi-select' | 'ref' | 'multi-ref' | 'date' | 'datetime' | 'address' | 'address-search' | 'contact'
  | 'string-array' | 'file' | 'image'
  /** Rendered by `FieldConfig.render`; never saved as-is, and left out of the
   *  detail panel, PDF and bulk edit. The component writes the real form
   *  keys itself (list them in `extraSaveKeys` if they aren't fields). */
  | 'custom';

export interface FieldConfig {
  name: string;                  // field name / dot-path
  label: string;
  type: FieldType;
  required?: boolean;
  options?: Array<{ value: string; label: string }>;
  /** For type='select': fetch options from this endpoint (returns `[{id,name}]`).
   *  Pass a function to compute the URL from current form values (e.g. derive
   *  the model slug from another field). Return null/undefined to disable. */
  optionsEndpoint?: string | ((formValues: Record<string, any>) => string | null | undefined);
  refEndpoint?: string;          // for 'ref'
  refLabel?: string;             // display field for ref
  /** For type='ref' only. When set, the LookupField still renders the row's
   *  `refLabel` as the option text, but the value persisted to the form is
   *  the row's `refValueField` (e.g. `'providerName'`) — NOT the row id.
   *  Use this when the underlying schema column is a plain string that
   *  mirrors a name from a master-data table (e.g. `Asset.trackingProvider`
   *  stores 'EROAD' as text rather than an ObjectId). Lets you swap a
   *  hardcoded select for a LookupField without migrating storage. */
  refValueField?: string;
  /** For type='custom' only: renders the field's UI. `setField` writes any
   *  form key (dot-paths allowed). */
  render?: (ctx: { formValues: Record<string, any>; setField: (name: string, value: any) => void; isEdit: boolean }) => React.ReactNode;
  /** Extra static query params merged into the ref/multi-ref list URL. Use
   *  for things like `{ status: 'Active' }` or alphabetical sorting
   *  (`{ sort: 'fullName', order: 'asc' }`). Combined with any `dependsOn`-
   *  derived filter at render time. */
  refQuery?: Record<string, string>;
  placeholder?: string;
  /** Cascading filter for ref/multi-ref. When set, the dropdown is filtered by
   *  `?<refField>=<value-of-formField>`, and is disabled until that form field
   *  has a value. Use to chain dropdowns (e.g. servicePlan filtered by chosen asset). */
  dependsOn?: { formField: string; refField: string };
  /** For type='file' / 'image': S3 key prefix the new upload lands under
   *  (e.g. 'asset', 'vehicle-files'). Defaults to 'uploads'. */
  uploadPrefix?: string;
  /** For type='file' / 'image': accept attribute on the file input
   *  (defaults: 'image/*' for image, no restriction for file). */
  uploadAccept?: string;
  /** For type='image': name of a legacy sibling field on the same record
   *  that may carry a previous image as either a bare URL string or
   *  `<img src="…">` HTML. When the upload key (`value`) is null but the
   *  sibling has a usable URL, the form shows it as a read-only preview
   *  alongside an "Upload to replace" button. Used for legacy Zoho rows
   *  that never had their photo migrated to S3. */
  legacyPreviewField?: string;
  /** Optional grouping label. Consecutive fields sharing a section render
   *  under a section header in the create/edit form — same pattern Zoho
   *  uses to break long forms (Basic Info / Tracking / Registration etc.).
   *  Configs that don't set `section` on any field keep the flat layout
   *  for backwards-compatibility. */
  section?: string;
  /** Conditional visibility — when set, the field renders only if the
   *  predicate returns true for the current form values. Used for fields
   *  whose relevance depends on another field's value (e.g. provider-
   *  specific tracking IDs that should hide when a different provider is
   *  selected). Hidden fields are still in `config.fields` so their value
   *  in the underlying record is preserved on save; if you want to wipe
   *  a hidden field's value, do it in `onChange` of the controlling
   *  field. */
  visibleIf?: (formValues: Record<string, any>) => boolean;
  /** Action key that unlocks this field. Without the grant the value stays
   *  visible but the input is inert. Use for system-managed values like the
   *  per-provider IoT IDs (`eroadId`, `blackhawkId`, …) where a fat-finger
   *  edit can cross-link an asset to the wrong vehicle in the provider's
   *  fleet.
   *
   *  Was a boolean keyed on `systemProfile`. That value was never maintained
   *  as an access axis — production ran 186 employees on 'Fleet User' and one
   *  on 'Admin' — so the lock was absolute rather than delegable. */
  unlockAction?: string;
  /** For ref fields with a `dependsOn` cascade: when the dependent
   *  lookup returns exactly ONE matching option and the field is
   *  currently empty, auto-select it. Skips the obvious "pick the
   *  only choice" click — used on Job Card / Log Service for Service
   *  Plan after an Asset is picked. */
  autoSelectIfSingle?: boolean;
}

// `extractImageUrl` and `UploadField` were extracted to `./upload-field.tsx`
// so the inline task editors (which this module imports) can render the
// upload widget without an import cycle back through here. Re-exported for
// the existing `@/components/shared/crud-page` import sites.
export { extractImageUrl, UploadField };

// Column primitives now live in `report-controls.tsx` so hand-rolled report
// pages and CrudPage-driven reports share a single source of truth. Re-
// exported here for backwards compatibility with existing imports.
export type { ColumnFilterType, ColumnConfig } from './report-controls';
import {
  ColumnHeader, SearchDrawer,
  type ColumnConfig,
} from './report-controls';

export interface RelatedListConfig {
  title: string;
  endpoint: string;              // e.g. '/servicing/schedules'
  filterField: string;           // e.g. 'servicePlan' → ?servicePlan=<parentId>
  /** Static extra query params merged into the list URL (e.g. for the audit
   *  log which is shared across entity types and needs `entityType=Asset`
   *  alongside `entityId=<id>`). Combined with the dynamic filterField. */
  filterExtra?: Record<string, string>;
  /** Optional client-side ordering applied after fetch. The list endpoint
   *  defaults to sorting by `createdAt`, which is arbitrary for sections whose
   *  natural order is something else (Upcoming services reads best as
   *  A-Service, B-Service, C-Service, D-Service). Sorting on a populated path
   *  can't be done server-side — Mongo sorts before `populate` resolves the
   *  ref — so the comparator runs here, over the rows already fetched. */
  sortBy?: (a: any, b: any) => number;
  columns: ColumnConfig[];
  /** Optional pluggable inline editor. When set, the read-only table is replaced
   *  with a specialized component (e.g. inline-editable checklist). */
  inlineEditor?: 'service-checklist' | 'onboarding-tasks' | 'transfer-tasks';
  /** When set, rows are clickable and open an edit dialog using these fields.
   *  Saved via PUT `${endpoint}/:id`. Allows editing related records inline. */
  rowEditFields?: FieldConfig[];
  /** Suppress the row-click edit dialog while keeping `rowEditFields` available
   *  for `+ Add` (rowCreate). Use when the list already exposes a dedicated
   *  per-row edit affordance (e.g. an "Edit" column link) and a second
   *  click target on the row itself would be confusing. */
  disableRowClickEdit?: boolean;
  /** Singular noun for the edit dialog title (e.g. "Task"). Defaults to "Item". */
  rowEditSingular?: string;
  /** When true (and `rowEditFields` is set), show a `+ Add` button above the
   *  table that opens the same dialog with empty values. On submit a new
   *  record is POSTed to `endpoint` with the parent reference auto-filled
   *  via `filterField: parentId`. Use for nested edit flows like
   *  Schedule → Checklist Items. */
  rowCreate?: boolean;
  /** Extra static field values merged into the create payload, beyond the
   *  `filterField` parent ref. E.g. `{ fromExport: false }`. */
  rowCreateDefaults?: Record<string, unknown>;
  /** Suppress this related list while the parent record is being edited.
   *  Honoured only when the parent config sets `showRelatedListsInEdit:
   *  true` — use to keep operational sub-lists (schedules) visible while
   *  hiding noisy history lists during edit mode. The list still appears
   *  on the read-only DetailDialog. */
  hideInEdit?: boolean;
}

export type ActionVariant = 'default' | 'destructive' | 'outline' | 'secondary';

export interface BulkActionConfig {
  label: string;
  /** POST endpoint — e.g. '/servicing/history/bulk-approve'. Mutually
   *  exclusive with `onClick`. */
  endpoint?: string;
  idsKey?: string;               // body key for selected IDs (default 'ids')
  confirm?: string;              // confirm message
  promptReason?: boolean;        // prompt for rejection reason
  variant?: ActionVariant;
  /** If set, the button is hidden unless the user has this action key granted. */
  actionKey?: string;
  /** Hide the button when NONE of the selected rows can take the action —
   *  e.g. Approve on Service History, where a record belonging to a job card
   *  must be approved from that card. Without it a user selects linked records,
   *  clicks Approve, and gets "0 approved, N skipped": correct, but it looks
   *  broken.
   *
   *  FAIL-OPEN by design. Selection is only cleared on a successful mutation,
   *  not on page change, so it can span pages and rows from other pages are not
   *  inspectable here. If ANY selected id cannot be resolved to a row the
   *  button is SHOWN — hiding an action on incomplete information is worse than
   *  showing one the server will partially skip, and the server stays the
   *  authority either way. */
  enabledFor?: (row: any) => boolean;
  /** Custom handler — receives the selected ids + helpers so the page can
   *  open a picker / confirm / dispatch its own mutation. Mutually
   *  exclusive with `endpoint`. */
  onClick?: (
    ids: string[],
    helpers: {
      refetch: () => void;
      navigate: (path: string) => void;
      clearSelection: () => void;
    },
  ) => void | Promise<void>;
}

/**
 * Standalone header-level button — e.g. "Sync EROAD" on the Assets report.
 * Not tied to row selection. Either provide `endpoint` (simple POST) or
 * `onClick` (custom handler — receives a refetch callback for the table).
 */
export interface TabConfig {
  /** Visible label shown on the tab. */
  label: string;
  /** Filter applied while this tab is active. Each entry becomes a URL
   *  query param the server already understands via its `filterFields`.
   *  Empty / omitted means "no filter" — typically the first tab labelled
   *  "All". To match "field is unset / empty", pass an empty string.
   *  Mutually exclusive with `to`. */
  filter?: Record<string, string>;
  /** Cross-route tab — clicking navigates to this path instead of mutating
   *  the current URL's query string. Active when the current pathname
   *  starts with this value. Use to put sibling reports (e.g. Active /
   *  Archived / Disposed assets) under one tab strip even though each
   *  lives on its own endpoint with its own permission slug. */
  to?: string;
}

export interface ToolbarActionConfig {
  label: string;
  /** If set, the button is hidden unless the user has this action key granted. */
  actionKey?: string;
  variant?: ActionVariant;
  confirm?: string;
  /** Simple POST to this endpoint. Mutually exclusive with `onClick`. */
  endpoint?: string;
  /** Static body to send with the simple POST. */
  body?: Record<string, unknown>;
  /** Custom handler. Receives helpers so the action can navigate or invalidate the report after async work. */
  onClick?: (ctx: { refetch: () => void; navigate: (path: string) => void }) => void | Promise<void>;
  /** Re-evaluated on every render — lets a toggle reflect URL or pref state
   *  in its label and variant. Falls back to `label`/`variant` when absent. */
  dynamicLabel?: () => string;
  dynamicVariant?: () => ActionVariant;
  /** When the toolbar action needs richer UI than a button + confirm, the
   *  config can supply a React component to render. The CrudPage opens it
   *  when the button is clicked and provides close / refetch callbacks.
   *  Mutually exclusive with `endpoint` / `onClick`. */
  customDialog?: React.ComponentType<{
    onClose: () => void;
    onComplete?: () => void;
  }>;
}

export interface RowActionPick {
  /** Ref list endpoint — e.g. '/servicing/schedules'. */
  endpoint: string;
  /** Field on the fetched records to display as the option label. */
  refLabel: string;
  /** Body key used when POSTing — e.g. 'servicePlanSchedule'. */
  bodyKey: string;
  /** Dialog title shown above the picker. */
  title?: string;
  /** When true, the picker allows selecting multiple values; the body key receives an array of ids. */
  multi?: boolean;
}

export interface RowActionConfig {
  label: string;
  /** Precondition the SERVER also enforces, surfaced as an explanation instead
   *  of a failed request. Return null when the action may run.
   *
   *  Prefer this over hiding the action with `visible` when the user would
   *  reasonably expect it to be there: an action that silently disappears reads
   *  as a bug or a missing permission, and the user has no way to learn why.
   *  Service History approval is the case that prompted it — a record belonging
   *  to a job card must be approved from that card, and hiding the button left
   *  people looking at "Approve selected" in the toolbar wondering where the
   *  per-row Approve went.
   *
   *  `goTo` turns the dialog's confirm button into a link, so the message can
   *  send the user where the action IS available rather than dead-ending. */
  blocked?: (row: any) => {
    title?: string;
    message: string;
    goTo?: string;
    goToLabel?: string;
  } | null;
  /** When `link` is set, clicking this action navigates instead of POSTing.
   *  Use a callback for row-dependent paths (e.g. (row) => `/fleet/job-cards/${row.id}/service`). */
  link?: string | ((row: any) => string);
  endpoint?: string;             // e.g. '/servicing/history/:id/approve' — ':id' is substituted (ignored when link is set)
  method?: 'POST' | 'PUT' | 'DELETE';
  /** Static request body sent with the action — useful for "set status to X" buttons. */
  body?: Record<string, unknown>;
  confirm?: string;
  promptReason?: boolean;
  /** If set, opens a picker dialog populated from a ref endpoint; selection is POSTed as `bodyKey`. */
  pick?: RowActionPick;
  /** Render a custom dialog instead of POSTing directly. Receives the row,
   *  a close callback, and a refetch callback. Used for actions that need
   *  richer form UI than `pick` or `promptReason` provide — e.g. the
   *  Offboard Asset form which needs an outcome select + two free-text
   *  fields + an IoT-notify toggle. */
  customDialog?: React.ComponentType<{
    row: any;
    onClose: () => void;
    onComplete?: () => void;
  }>;
  /** Custom callback for actions whose behaviour lives entirely on the
   *  host page (e.g. open a page-managed Dialog with pre-filled state).
   *  Mutually exclusive with `link` / `endpoint` / `pick` / `customDialog`. */
  onClick?: (
    row: any,
    helpers: {
      refetch: () => void;
      navigate: (path: string) => void;
    },
  ) => void | Promise<void>;
  variant?: ActionVariant;
  visible?: (row: any) => boolean;
  /** If set, the button is hidden unless the user has this action key granted. */
  actionKey?: string;
  /** Render the action as an inline icon button in the row's Actions column
   *  (next to the Edit pencil / Delete trash) in addition to the right-click
   *  menu and edit-dialog Actions dropdown. Use for high-frequency actions
   *  that benefit from one-click access without opening the edit panel. */
  inline?: boolean;
  /** Icon shown when `inline: true`. Required for inline actions — the
   *  cell shows icons-only to keep row height stable. */
  icon?: React.ReactNode;
}

export interface FormExtrasContext {
  isEdit: boolean;
  parentId?: string;
  formValues: Record<string, any>;
  setFormValues: React.Dispatch<React.SetStateAction<Record<string, any>>>;
}

export interface CrudConfig {
  title: string;
  singular: string;
  endpoint: string;              // e.g. '/master-data/companies'
  /** Resource slug used to gate the +New / Edit / Delete buttons against the
   *  user's role-permission matrix. Matches `Resource.slug` (form-type, e.g.
   *  `companies`). When omitted, buttons are shown to all authenticated users —
   *  the server still enforces if its `resourceSlug` is wired. */
  formSlug?: string;
  /** Page-type Resource slug used by `<RequireResource>` wrappers to deny
   *  access at the route level (e.g. `assets_page`). Distinct from `formSlug`
   *  so a user can be allowed to view the report but not edit the underlying
   *  form, or vice versa. */
  pageSlug?: string;
  /** When false, the "+ New" button is hidden. Use for catalogue tables. */
  canCreate?: boolean;
  /** When false, the row Edit pencil (and bulk Edit) is hidden — for read-only
   *  reports like audit logs. */
  canEdit?: boolean;
  /** When false, the row delete button is hidden. */
  canDelete?: boolean;
  /** Document field carrying ownership (e.g. `zohoAddedByEmployee`, `driver`,
   *  `assignedTo`). When the user's edit/delete grant is `own` (not `all`),
   *  the inline edit/delete icons are shown only on rows they own — compared
   *  against the current user id (array fields use membership). When omitted,
   *  icons stay visible under `own` and the server enforces. Mirrors the
   *  server's `ownerField` for the resource. */
  ownerField?: string;
  /** Opt out of the built-in "Edit Selected" bulk action — defaults to on
   *  for every CRUD report. Gated by the same `edit` permission as the per-
   *  row Edit. */
  disableBulkEdit?: boolean;
  /** Opt out of the built-in "Delete Selected" bulk action — defaults to on
   *  for every report that allows row delete. Gated by the same `delete`
   *  permission as the per-row Delete. */
  disableBulkDelete?: boolean;
  /** Action key required to see the row Edit pencil + the "+ New" button.
   *  Without it the user is routed through `rowActions` (e.g. an "Open
   *  wizard" link) — used to force a guided flow. */
  editAction?: string;
  /** When true, clicking anywhere on a desktop table row (outside the
   *  checkbox and action-button columns) opens the inline edit form —
   *  same as the existing mobile-card behaviour. Mobile already does this
   *  unconditionally; this flag controls the desktop side.
   *  Forces the legacy "go straight to edit" behaviour even when
   *  `clickRowToView` would otherwise be active. */
  clickRowToEdit?: boolean;
  /** When true (default), clicking a row opens a read-only DetailDialog with
   *  an Edit button instead of jumping into edit mode. Set false to disable
   *  the detail view entirely (rare — only useful for terminal action lists
   *  where there's nothing to read). Overridden by `clickRowToEdit`. */
  clickRowToView?: boolean;
  /** When set, the standard "Edit" action (pen icon, context-menu Edit,
   *  detail-panel Edit button, ?edit= deep-link) navigates to the URL
   *  returned by this callback instead of opening the inline edit form.
   *  Use when the resource's edit flow lives on a dedicated page — e.g.
   *  Service History rows edit by deep-linking into the linked JobCard's
   *  service-execution page (Start Service). Returning null/undefined
   *  falls back to the default inline form. */
  editLink?: (row: any) => string | null | undefined;
  /** Intercept the standard "Edit" action with a custom callback (e.g.
   *  open a page-managed Dialog). Mutually exclusive with `editLink`.
   *  Returning anything skips the built-in inline edit form. */
  editOverride?: (row: any) => void;
  /** Intercept the "+ New" button so the page can open its own create
   *  Dialog instead of the built-in inline form. */
  createOverride?: () => void;
  /** Optional horizontal tab strip rendered above the report table. Each
   *  tab pre-applies a filter via URL search params (so the choice is
   *  shareable and deep-linkable). The tab whose filter matches the
   *  current URL is shown active; the first tab matches when no tab
   *  filter is set. Filter keys must be on the server's `filterFields`
   *  whitelist for the resource. */
  tabs?: TabConfig[];
  /** Optional secondary tab strip rendered below the primary one. Same
   *  semantics as `tabs` (filter-based or `to`-based). Use for two-axis
   *  navigation — e.g. lifecycle (primary) × IoT provider (secondary)
   *  on the Assets report. */
  secondaryTabs?: TabConfig[];
  columns: ColumnConfig[];
  fields: FieldConfig[];
  /** Override the read-only DetailDialog subtitle. By default the panel walks
   *  a chain of well-known label fields and falls back to the raw id — which
   *  is meaningless for records with no natural label (e.g. audit-log rows).
   *  Return a human string to show instead; return null/undefined to keep the
   *  default chain. */
  detailTitle?: (row: any) => string | null | undefined;
  /** Render custom read-only content in the DetailDialog body INSTEAD of the
   *  per-field grid. Use for report-style resources whose meaningful content
   *  isn't a flat set of `fields` (e.g. the Asset Audit Trail's colored change
   *  diff). Related lists still render below when configured. */
  detailContent?: (row: any) => React.ReactNode;
  defaultValues?: Record<string, any>;
  relatedLists?: RelatedListConfig[];
  /** By default related lists render only on the read-only DetailDialog so
   *  the edit form stays focused on the parent record's own fields. Set to
   *  `true` for resources where the related context is essential mid-edit
   *  (e.g. Service Plan → schedules — operators expect to retune a
   *  schedule's cadence while editing the plan itself). */
  showRelatedListsInEdit?: boolean;
  bulkActions?: BulkActionConfig[];
  rowActions?: RowActionConfig[];
  toolbarActions?: ToolbarActionConfig[];
  /** Optional React component rendered inside the create/edit dialog, between the
   *  fields and the related lists. Use this to embed staged subforms. */
  FormExtras?: React.ComponentType<FormExtrasContext>;
  /** Additional top-level keys pulled from `formValues` into the submit body —
   *  used when `FormExtras` manages embedded sub-documents (e.g. nested email
   *  templates) that aren't enumerated in `fields`. */
  extraSaveKeys?: string[];
  /** Hook fired after a successful create/update. Use with FormExtras to persist
   *  staged child records (e.g. POST line items under the new parent id). */
  afterSave?: (id: string, formValues: Record<string, any>, isCreate: boolean) => Promise<void>;
  /** Optional client-side guard run on every render and again at submit.
   *  Return a `{ fieldName: message }` map for cross-field rules the
   *  per-field renderer can't express (e.g. endTime > startTime). Messages
   *  render inline under the named field — same pattern the past-service
   *  form uses — and any non-empty map blocks the Create/Update click. */
  validate?: (formValues: Record<string, any>) => Record<string, string> | null | undefined;
  /** A second submit button beside Create, which creates the record and then
   *  POSTs to `endpoint` with the new id — e.g. "Create & Approve" on a job
   *  card. Create-only (never shown when editing) and hidden unless
   *  `actionKey` is granted, so it cannot offer a transition the server would
   *  refuse. `blockedReason` returns a string to disable it with an
   *  explanation, for preconditions the server also enforces. */
  createAndThen?: {
    label: string;
    pendingLabel?: string;
    actionKey: string;
    /** `:id` is replaced with the id of the record just created. */
    endpoint: string;
    /** Body text of the confirmation modal. Omit to skip confirmation. */
    confirm?: string;
    /** Heading of that modal. Defaults to `${label}?`. */
    confirmTitle?: string;
    blockedReason?: (formValues: Record<string, any>) => string | null;
  };
}

// ===== Utilities =====
function get(obj: any, path: string): any {
  return path.split('.').reduce((a, k) => (a == null ? a : a[k]), obj);
}
function set(obj: any, path: string, value: any): any {
  const out = { ...obj };
  const parts = path.split('.');
  let cur = out;
  for (let i = 0; i < parts.length - 1; i++) {
    cur[parts[i]] = { ...(cur[parts[i]] || {}) };
    cur = cur[parts[i]];
  }
  cur[parts[parts.length - 1]] = value;
  return out;
}

/** The address subform's fields and labels. Exported because the dispatch
 *  edit dialog renders the same subform and the two must not drift — a field
 *  present in one and missing from the other is invisible on whichever screen
 *  omits it, which is exactly the bug this export prevents. */
export const ADDRESS_FIELDS: Array<[string, string]> = [
  ['addressLine1', 'Address Line 1'],
  ['addressLine2', 'Address Line 2'],
  ['city', 'City'],
  ['stateProvince', 'State/Province'],
  ['postalCode', 'Postal Code'],
  ['country', 'Country'],
];

// Sub-fields rendered for `type: 'contact'`. `prefix` was dropped — Mr./
// Mrs./etc. is rarely useful in the operational flows and just makes the
// row narrower. Any prefix value already in the DB (from Zoho migration)
// is preserved on save because the form only overwrites the keys it
// renders.
const CONTACT_FIELDS: Array<[string, string]> = [
  ['firstName', 'First Name'],
  ['lastName', 'Last Name'],
];

function refToId(v: unknown): string | null {
  if (v == null) return null;
  if (typeof v === 'string') return v;
  if (typeof v === 'object') {
    const obj = v as Record<string, unknown>;
    return (obj.id as string) || (obj._id as string) || null;
  }
  return null;
}

/** Combine two permission scopes, keeping the most permissive: all > own > none. */
function maxScope(a: 'all' | 'own' | 'none', b: 'all' | 'own' | 'none'): 'all' | 'own' | 'none' {
  if (a === 'all' || b === 'all') return 'all';
  if (a === 'own' || b === 'own') return 'own';
  return 'none';
}

function refsToIds(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v.map(refToId).filter((x): x is string => !!x);
}

// RefSelect / MultiRefSelect were extracted to `./lookup-field.tsx` as
// LookupField / MultiLookupField so the wizards and other hand-rolled
// subforms could share the same Popover-based UX. The Field renderer
// below imports them directly.


function AsyncSelectField({
  url, value, onChange, required, placeholder, disabledReason,
}: {
  url: string | null;
  value: unknown;
  onChange: (v: string | null) => void;
  required?: boolean;
  placeholder?: string;
  disabledReason?: string;
}) {
  const { data } = useQuery({
    queryKey: ['async-options', url],
    queryFn: async () => (await api<{ id: string; name: string }[]>(url!)).data || [],
    enabled: !!url,
  });
  const items = data || [];
  const current = (value as string | null) ?? null;
  const inList = current && items.some((it) => it.id === current);
  return (
    <Select
      value={current || '__null__'}
      onValueChange={(v) => onChange(v === '__null__' ? null : v)}
      disabled={!url}
    >
      <SelectTrigger><SelectValue placeholder={!url ? (disabledReason || '— Select —') : (placeholder || '— Select —')} /></SelectTrigger>
      <SelectContent>
        {!required && <SelectItem value="__null__">— None —</SelectItem>}
        {!inList && current && <SelectItem value={current}>{current} (legacy)</SelectItem>}
        {items.map((o) => (
          <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** LookupField wrapper used when a ref field's underlying column stores a
 *  string drawn from a master-data table (e.g. Asset.trackingProvider holds
 *  'EROAD' as text). Maps the picked row's id ↔ its `valueField` value so
 *  the popover/search UX is identical to other refs, but the form persists
 *  the name string the schema expects.
 *
 *  Reads the same /:endpoint as LookupField fetches internally — the second
 *  small fetch here keeps the row lookup co-located with the mapping logic
 *  (LookupField itself only exposes ids in its API). The list is tiny (e.g.
 *  IoT suppliers = 2 rows) so the extra request is negligible. */
function RefValueLookupField({
  endpoint, labelKey, valueField, value, onChange,
  filter, disabled, disabledReason, required, placeholder,
}: {
  endpoint: string; labelKey: string; valueField: string;
  value: string | null;
  onChange: (next: string | null) => void;
  filter?: Record<string, string>;
  disabled?: boolean;
  disabledReason?: string;
  required?: boolean;
  placeholder?: string;
}) {
  const filterKey = filter ? JSON.stringify(filter) : '';
  const { data: rows = [] } = useQuery<any[]>({
    queryKey: ['ref-value', endpoint, filterKey],
    queryFn: async () => {
      const qs = new URLSearchParams(filter ?? {});
      qs.set('limit', '200');
      return (await api<any[]>(`${endpoint}?${qs.toString()}`)).data || [];
    },
  });
  // Stored value is the string name; resolve it to an id so LookupField can
  // show the matching row as selected. Falls through to null if no match
  // (legacy values from a renamed/removed master-data row).
  const idForValue = (value && rows.find((r) => r?.[valueField] === value)?.id) || null;
  return (
    <LookupField
      endpoint={endpoint}
      labelKey={labelKey}
      value={idForValue}
      onChange={(pickedId) => {
        if (!pickedId) { onChange(null); return; }
        const picked = rows.find((r) => r.id === pickedId);
        onChange((picked?.[valueField] as string | undefined) ?? null);
      }}
      filter={filter}
      disabled={disabled}
      disabledReason={disabledReason}
      required={required}
      placeholder={placeholder}
    />
  );
}

function Field({ f, value, onChange, formValues, invalid }: { f: FieldConfig; value: any; onChange: (v: any) => void; formValues?: Record<string, any>; invalid?: boolean }) {
  if (f.type === 'boolean') {
    return (
      <div className="flex items-center gap-2">
        <Checkbox checked={!!value} onCheckedChange={(c) => onChange(!!c)} />
        <Label>{f.label}</Label>
      </div>
    );
  }
  if (f.type === 'select') {
    if (f.optionsEndpoint) {
      const url = typeof f.optionsEndpoint === 'function'
        ? (f.optionsEndpoint(formValues ?? {}) ?? null)
        : f.optionsEndpoint;
      return <AsyncSelectField
        url={url}
        value={value} onChange={onChange}
        required={f.required}
        placeholder={f.placeholder}
        disabledReason={!url ? 'Pick a parent field first' : undefined}
      />;
    }
    return (
      <Select value={value ?? ''} onValueChange={(v) => onChange(v === '__null__' ? null : v)}>
        <SelectTrigger aria-label={f.label}><SelectValue placeholder={f.placeholder || '— Select —'} /></SelectTrigger>
        <SelectContent>
          {!f.required && <SelectItem value="__null__">— None —</SelectItem>}
          {f.options?.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }
  if (f.type === 'multi-select') {
    // Static-options multi-select. Renders the configured `options` as
    // toggleable chips and stores the picked values as a string[] on
    // the record. Used for Zoho-parity enum fields like Job Type where
    // the values are fixed (Scheduled Service / WoF / CoF / …) and a
    // single record can hold several at once.
    const list: string[] = Array.isArray(value) ? value : [];
    const opts = f.options ?? [];
    return (
      <div className="flex flex-wrap gap-2">
        {opts.map((opt) => {
          const on = list.includes(opt.value);
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(on ? list.filter((v) => v !== opt.value) : [...list, opt.value])}
              className={cn(
                'px-3 py-1 rounded-full text-xs border transition-colors',
                on ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-accent',
              )}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    );
  }
  if (f.type === 'ref' && f.refEndpoint) {
    let filter: Record<string, string> | undefined = f.refQuery ? { ...f.refQuery } : undefined;
    let disabled = false;
    let disabledReason: string | undefined;
    if (f.dependsOn && formValues) {
      const parentValue = refToId(get(formValues, f.dependsOn.formField));
      // Dynamic dependsOn filter wins over the static refQuery if both name
      // the same key (more specific runtime context).
      if (parentValue) filter = { ...(filter ?? {}), [f.dependsOn.refField]: parentValue };
      else { disabled = true; disabledReason = `Pick ${f.dependsOn.formField} first`; }
    }
    // `refValueField` mode: the underlying schema column stores a plain
    // string drawn from a master-data table (e.g. Asset.trackingProvider
    // stores 'EROAD' as text, not an ObjectId). Wrap LookupField so the
    // form persists the picked row's `refValueField` value rather than
    // its id — no storage migration needed.
    if (f.refValueField) {
      return <RefValueLookupField
        endpoint={f.refEndpoint}
        labelKey={f.refLabel || 'name'}
        valueField={f.refValueField}
        filter={filter}
        disabled={disabled}
        disabledReason={disabledReason}
        required={f.required}
        placeholder={f.placeholder}
        value={value as string | null}
        onChange={onChange}
      />;
    }
    return <LookupField
      endpoint={f.refEndpoint} labelKey={f.refLabel || 'name'}
      value={value} onChange={onChange}
      filter={filter} disabled={disabled} disabledReason={disabledReason}
      required={f.required}
      autoSelectIfSingle={f.autoSelectIfSingle}
    />;
  }
  if (f.type === 'multi-ref' && f.refEndpoint) {
    return <MultiLookupField endpoint={f.refEndpoint} labelKey={f.refLabel || 'name'} value={value} onChange={onChange} filter={f.refQuery} />;
  }
  if (f.type === 'address-search') {
    // Single-line variant: just the Mapbox typeahead. The structured
    // address (line1, line2/suburb, city, region, postcode, country)
    // is still saved — the user just doesn't see the 6-field grid.
    // Used by Dispatch Jobs so the CRUD form matches the Schedule
    // drag-create dialog visually.
    const addr = (value || {}) as Record<string, string>;
    return (
      <AddressAutocomplete
        value={addr.addressLine1 ?? ''}
        onChange={(v) => onChange({ ...addr, addressLine1: v })}
        onSelect={(feat) => {
          // Single-line UI ⇒ the visible value should be the full picked
          // address ("11 Wairau Road, Milford, Auckland 0620, NZ"), not
          // just the street. We override line1 with `place_name` after
          // letting `extractAddressParts` populate the structured parts
          // (line2/city/region/postcode/country) behind the scenes.
          const parts = extractAddressParts(feat);
          onChange({ ...addr, ...parts, addressLine1: feat.place_name });
        }}
        placeholder="Start typing an address…"
        ariaLabel={f.label}
      />
    );
  }
  if (f.type === 'address') {
    const addr = value || {};
    const setAddr = (patch: Record<string, unknown>) => onChange({ ...addr, ...patch });
    return (
      <div className="grid grid-cols-2 gap-2">
        {ADDRESS_FIELDS.map(([k, lbl]) => (
          <div key={k} className="col-span-2 md:col-span-1">
            <Label className="text-xs text-muted-foreground">{lbl}</Label>
            {k === 'addressLine1' ? (
              // Address Line 1 is the typeahead. Selecting a Mapbox
              // suggestion cascades the parsed parts (city, state,
              // postcode, country) into the rest of the subform — the
              // user can still tweak any field manually afterwards.
              <AddressAutocomplete
                value={addr[k] ?? ''}
                onChange={(v) => setAddr({ [k]: v })}
                onSelect={(feat) => setAddr({ ...extractAddressParts(feat) })}
                placeholder="Start typing an address…"
                ariaLabel={`${f.label} — ${lbl}`}
              />
            ) : (
              <Input aria-label={`${f.label} — ${lbl}`} value={addr[k] ?? ''} onChange={(e) => setAddr({ [k]: e.target.value })} />
            )}
          </div>
        ))}
      </div>
    );
  }
  if (f.type === 'contact') {
    const c = value || {};
    return (
      <div className="grid grid-cols-3 gap-2">
        {CONTACT_FIELDS.map(([k, lbl]) => (
          <div key={k}>
            <Label className="text-xs text-muted-foreground">{lbl}</Label>
            <Input aria-label={`${f.label} — ${lbl}`} value={c[k] ?? ''} onChange={(e) => onChange({ ...c, [k]: e.target.value })} />
          </div>
        ))}
      </div>
    );
  }
  if (f.type === 'string-array') {
    const list: string[] = Array.isArray(value) ? value : [];
    return (
      <div className="space-y-2">
        {list.length === 0 && (
          <div className="text-xs text-muted-foreground">No entries — click "Add" to create one.</div>
        )}
        {list.map((v, i) => (
          <div key={i} className="flex items-center gap-2">
            <Input
              type="text"
              value={v}
              placeholder={f.placeholder}
              onChange={(e) => {
                const next = [...list];
                next[i] = e.target.value;
                onChange(next);
              }}
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onChange(list.filter((_, j) => j !== i))}
            >
              Remove
            </Button>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onChange([...list, ''])}
        >
          + Add
        </Button>
      </div>
    );
  }
  if (f.type === 'textarea') {
    return (
      <textarea
        aria-label={f.label}
        className="flex w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        value={value ?? ''}
        placeholder={f.placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  if (f.type === 'file' || f.type === 'image') {
    const legacyImageUrl =
      f.type === 'image' && f.legacyPreviewField
        ? extractImageUrl(formValues?.[f.legacyPreviewField])
        : null;
    // Server convention: a *Key field is paired with a *Url sibling on
    // the read response (e.g. photoKey + photoUrl). Pass that pre-signed
    // URL through so we don't need an extra `/uploads/:key/url` round-
    // trip and so the preview survives even if that endpoint is down.
    const urlSibling = f.name.endsWith('Key') ? f.name.slice(0, -3) + 'Url' : null;
    const presignedUrl = urlSibling && typeof formValues?.[urlSibling] === 'string'
      ? (formValues[urlSibling] as string)
      : null;
    return (
      <UploadField
        f={f}
        value={typeof value === 'string' ? value : null}
        onChange={(key) => onChange(key)}
        legacyImageUrl={legacyImageUrl}
        presignedUrl={presignedUrl}
      />
    );
  }
  if (f.type === 'date' || f.type === 'datetime') {
    return (
      <NzDateTimeInput
        ariaLabel={f.label}
        withTime={f.type === 'datetime'}
        value={typeof value === 'string' || value instanceof Date ? value : null}
        onChange={(v) => onChange(v ? v.toISOString() : null)}
        invalid={invalid}
      />
    );
  }
  return (
    <Input
      aria-label={f.label}
      type={f.type === 'number' ? 'number' : f.type === 'email' ? 'email' : 'text'}
      value={value ?? ''}
      placeholder={f.placeholder}
      aria-invalid={invalid || undefined}
      className={invalid ? 'border-destructive focus-visible:ring-destructive' : undefined}
      onChange={(e) => onChange(f.type === 'number' ? (e.target.value === '' ? undefined : Number(e.target.value)) : e.target.value)}
    />
  );
}

// ===== CRUD Page =====
export function CrudPage({ config }: { config: CrudConfig }) {
  const { can } = usePermissions();
  // Page-level deny: when `pageSlug` is set and user lacks `view`, render the
  // 403 panel instead of the report. Mirrors `<RequireResource>` so callers
  // don't have to wrap CrudPage manually.
  if (config.pageSlug && !can(config.pageSlug, 'view')) {
    return (
      <div className="p-8 max-w-xl">
        <h1 className="text-2xl font-semibold mb-2">Access denied</h1>
        <p className="text-muted-foreground text-sm">
          Your role does not grant view access to this page.
        </p>
      </div>
    );
  }
  // Key on the resource endpoint so navigating to a different report
  // (e.g. /fleet-ops/assets → /fleet-ops/job-cards) unmounts the previous
  // CrudPageInner and remounts a fresh one. This clears the detail-side-
  // panel state, search input, pagination, etc. — without it, React reuses
  // the same instance under the same route pattern and the panel from the
  // previous report would linger on the new one.
  return <CrudPageInner key={config.endpoint} config={config} />;
}

function CrudPageInner({ config }: { config: CrudConfig }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [search, setSearch] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [editing, setEditing] = React.useState<any | null>(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  /** The row currently shown in the read-only DetailDialog. Null when no
   *  detail view is open. Mutually exclusive with `dialogOpen` (the edit
   *  flow) — clicking "Edit" inside the detail view closes detail and opens
   *  the edit dialog. */
  const [viewing, setViewing] = React.useState<any | null>(null);
  /** Pending right-click context menu — set on row contextmenu, cleared on
   *  selection or dismissal. */
  const [ctxMenu, setCtxMenu] = React.useState<{ row: any; x: number; y: number } | null>(null);
  const [formValues, setFormValues] = React.useState<Record<string, any>>({});
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const { actions: userActions } = useUserActions();
  const { can, scope } = usePermissions();
  const currentUserId = useAuthStore((s) => s.employee?.id ?? null);

  const isGranted = React.useCallback(
    (key?: string) => !key || userActions.has(key),
    [userActions],
  );

  // Resource-level gates. When `formSlug` is omitted, fall back to permissive —
  // the server still rejects if its `resourceSlug` is wired.
  // `editAction` collapses add+edit to holders of that action — used when a
  // resource has a guided alternative (e.g. the Onboard Asset wizard) that
  // everyone else should be funnelled through.
  const adminGate = !config.editAction || isGranted(config.editAction);
  // Add / Edit / Delete can be granted on EITHER the form (e.g. `assets`)
  // or the report (e.g. `assets_page`). Roles in this app almost always
  // attach Own-scope perms (viewOwnRecord / editOwnRecord) to the *report*
  // slug — so checking only `formSlug` like the old code did would mean a
  // technician with `editOwnRecord` on `services_page` saw no Edit button.
  // If neither slug is wired, default to permissive — the server still
  // enforces via its own `resourceSlug`.
  const hasAnySlug = !!config.formSlug || !!config.pageSlug;
  const addGrantedByForm = !!config.formSlug && can(config.formSlug, 'add');
  const addGrantedByPage = !!config.pageSlug && can(config.pageSlug, 'add');
  const editGrantedByForm = !!config.formSlug && can(config.formSlug, 'edit');
  const editGrantedByPage = !!config.pageSlug && can(config.pageSlug, 'edit');
  const deleteGrantedByForm = !!config.formSlug && can(config.formSlug, 'delete');
  const deleteGrantedByPage = !!config.pageSlug && can(config.pageSlug, 'delete');
  const canAdd    = adminGate && (!hasAnySlug || addGrantedByForm || addGrantedByPage);
  const canEdit   = config.canEdit !== false && adminGate && (!hasAnySlug || editGrantedByForm || editGrantedByPage);
  const canRemove = !hasAnySlug || deleteGrantedByForm || deleteGrantedByPage;

  // Per-row icon gating for `own`-scoped grants. `canEdit`/`canRemove` above
  // decide whether the feature exists at all (and gate bulk actions, where
  // the server filters ownership); these refine the INLINE row icons so we
  // don't show an Edit/Delete affordance on a row the user can't actually
  // touch under an `own` grant. Fail-open: with no `ownerField`, or an
  // unreadable owner value, we leave the icon visible and let the server
  // enforce — we never hide an affordance the user might legitimately have.
  const editScope = hasAnySlug
    ? maxScope(config.formSlug ? scope(config.formSlug, 'edit') : 'none',
               config.pageSlug ? scope(config.pageSlug, 'edit') : 'none')
    : 'all';
  const deleteScope = hasAnySlug
    ? maxScope(config.formSlug ? scope(config.formSlug, 'delete') : 'none',
               config.pageSlug ? scope(config.pageSlug, 'delete') : 'none')
    : 'all';
  const ownsRow = React.useCallback((row: any): boolean => {
    if (!config.ownerField || !currentUserId) return true; // can't tell → show, server enforces
    // Field absent from the payload → can't evaluate → fail open (show).
    // Only hide when the owner field IS present and doesn't include us.
    if (!row || !(config.ownerField in row)) return true;
    const v = row[config.ownerField];
    if (v == null) return false;
    const ids = (Array.isArray(v) ? v : [v]).map(refToId).filter(Boolean);
    return ids.includes(currentUserId);
  }, [config.ownerField, currentUserId]);
  const canEditRow = React.useCallback(
    (row: any) => canEdit && (editScope !== 'own' || ownsRow(row)),
    [canEdit, editScope, ownsRow],
  );
  const canDeleteRow = React.useCallback(
    (row: any) => config.canDelete !== false && canRemove && (deleteScope !== 'own' || ownsRow(row)),
    [config.canDelete, canRemove, deleteScope, ownsRow],
  );

  const visibleBulkActions = React.useMemo(
    () => (config.bulkActions ?? []).filter((a) => isGranted(a.actionKey)),
    [config.bulkActions, isGranted],
  );
  // `effectiveBulkActions` is composed further down the function — *after*
  // bulkEditMut / bulkDeleteMut have been declared — so the built-in
  // handlers can close over them without a TDZ violation.
  const visibleToolbarActions = React.useMemo(
    () => (config.toolbarActions ?? []).filter((a) => isGranted(a.actionKey)),
    [config.toolbarActions, isGranted],
  );

  // ---- Column visibility (per-user, persisted server-side) ----------------
  const allColumnKeys = React.useMemo(
    () => config.columns.map((c) => c.key),
    [config.columns],
  );
  const defaultVisibleKeys = React.useMemo(
    () => config.columns.filter((c) => c.showByDefault !== false).map((c) => c.key),
    [config.columns],
  );
  const visibilityNamespace = `crud-cols:${config.endpoint}`;
  const { value: visibleKeys, setValue: setVisibleKeys, ready: visibilityReady } =
    usePreferences<string[]>(visibilityNamespace, defaultVisibleKeys);
  const visibleSet = React.useMemo(() => new Set(visibleKeys ?? defaultVisibleKeys), [visibleKeys, defaultVisibleKeys]);
  const visibleColumns = React.useMemo(
    () => config.columns.filter((c) => visibleSet.has(c.key)),
    [config.columns, visibleSet],
  );

  // ---- Per-column filters (per-user, persisted server-side) ----------------
  const filtersNamespace = `crud-filters:${config.endpoint}`;
  // `ready` matters: the saved value arrives from an async GET, and a deep-link
  // that seeds filters before it lands would be silently overwritten by it.
  // See the URL-driven filter seeding effect below.
  const { value: filtersRaw, setValue: setFilters, ready: filtersReady } =
    usePreferences<Record<string, string>>(filtersNamespace, {});
  const filters = filtersRaw ?? {};

  const filterableColumns = React.useMemo(
    () => config.columns.filter((c) => !!c.filterType),
    [config.columns],
  );

  // ---- Column widths (per-user, persisted) --------------------------------
  // Map of column key → pixels. When non-empty, the table switches to
  // `table-layout: fixed` so explicit widths actually stick. Reset by removing
  // the entry from the map (returns the column to natural width).
  const widthsNamespace = `crud-widths:${config.endpoint}`;
  const { value: colWidths, setValue: setColWidths } =
    usePreferences<Record<string, number>>(widthsNamespace, {});
  const hasAnyWidth = React.useMemo(() => Object.keys(colWidths ?? {}).length > 0, [colWidths]);

  function setColumnWidth(key: string, width: number) {
    setColWidths((prev) => ({ ...(prev ?? {}), [key]: Math.max(60, Math.round(width)) }));
  }

  // ---- Sort + group state (per-user, persisted server-side) ----------------
  // `group=true` triggers client-side grouping in the table body — consecutive
  // same-value rows render under a subheader.
  type SortState = { field: string; order: 'asc' | 'desc'; group: boolean } | null;
  const sortNamespace = `crud-sort:${config.endpoint}`;
  const { value: sortState, setValue: setSortState } = usePreferences<SortState>(sortNamespace, null);

  // ---- Search drawer ------------------------------------------------------
  // The drawer shows every filterable column with an expandable inline input.
  // Multiple columns can be expanded at once so users build a multi-column
  // filter without drilling in/out.
  const [searchDrawerOpen, setSearchDrawerOpen] = React.useState(false);
  // Tracks which column header's "Search" was clicked → drawer opens with
  // that row pre-expanded + scrolled into view. Null when drawer is
  // opened via the toolbar Search button (no specific column focused).
  const [searchFocusedColumn, setSearchFocusedColumn] = React.useState<string | null>(null);

  function colFilterField(c: ColumnConfig): string {
    if (c.filterField) return c.filterField;
    // Strip dotted segments — `company.name` → `company`. The factory matches
    // top-level fields; sub-fields can be targeted with explicit filterField.
    return c.key.split('.')[0];
  }
  function colSortField(c: ColumnConfig): string {
    return c.sortField ?? colFilterField(c);
  }

  // Build the server query string from the active filters. Each filterType has
  // its own contract — text uses `_contains`, dates/numbers `_gte`/`_lte`, etc.
  const filterParams = React.useMemo(() => {
    const out: Record<string, string> = {};
    for (const c of filterableColumns) {
      const field = colFilterField(c);
      // Op selector — when the user changes the operator we store the
      // choice in `filters[<field>_op]`. Defaults preserve the prior
      // single-operator behaviour per filter type.
      const op = filters[`${field}_op`] ?? '';
      if (c.filterType === 'text') {
        if (op === 'equals') {
          const v = (filters[field] ?? '').trim();
          if (v) out[field] = v;
        } else if (op === 'ne') {
          const v = (filters[`${field}_ne`] ?? '').trim();
          if (v) out[`${field}_ne`] = v;
        } else if (op === 'ncontains') {
          const v = (filters[`${field}_ncontains`] ?? '').trim();
          if (v) out[`${field}_ncontains`] = v;
        } else {
          const v = (filters[`${field}_contains`] ?? '').trim();
          if (v) out[`${field}_contains`] = v;
        }
      } else if (c.filterType === 'enum' || c.filterType === 'boolean') {
        const v = (filters[field] ?? '').trim();
        if (v) out[field] = v;
      } else if (c.filterType === 'ref') {
        // Slot per op — matches the per-op storage in ColumnFilterInput so
        // the picker and free-text inputs each read/write their own state.
        //   equals    → `field`              (picker → single ObjectId)
        //   ne        → `field_ne`           (picker → single ObjectId, server $ne)
        //   contains  → `field_contains`     (free-text → server refTextFilters → $in matching ids)
        //   ncontains → `field_ncontains`    (free-text → server refTextFilters → $nin matching ids)
        if (op === 'ne') {
          const v = (filters[`${field}_ne`] ?? '').trim();
          if (v) out[`${field}_ne`] = v;
        } else if (op === 'contains') {
          const v = (filters[`${field}_contains`] ?? '').trim();
          if (v) out[`${field}_contains`] = v;
        } else if (op === 'ncontains') {
          const v = (filters[`${field}_ncontains`] ?? '').trim();
          if (v) out[`${field}_ncontains`] = v;
        } else {
          // Default `equals` — single id, exact match.
          const v = (filters[field] ?? '').trim();
          if (v) out[field] = v;
        }
      } else if (c.filterType === 'date' || c.filterType === 'number') {
        // Operator-aware variants. Default `between` keeps the legacy two-input range.
        const eq = (filters[field] ?? '').trim();
        const gte = (filters[`${field}_gte`] ?? '').trim();
        const lte = (filters[`${field}_lte`] ?? '').trim();
        if (op === 'on' && eq) {
          // "On <date>" expands to a same-day range so records with any
          // time-of-day still match — equality against a Date with hours,
          // minutes, seconds would almost never match.
          const d = new Date(eq);
          if (!Number.isNaN(d.getTime())) {
            // Resolve `eq` (the picker's YYYY-MM-DD) on the NZ calendar. `new Date`
            // reads it as UTC midnight and setHours() then snapped it to the
            // *browser's* day, landing a day early for anyone west of NZ.
            const start = parseNzWallToUtc(`${eq}T00:00`) ?? startOfNzDay(d);
            out[`${field}_gte`] = start.toISOString();
            out[`${field}_lte`] = endOfNzDay(start).toISOString();
          }
        } else if (op === 'eq') {
          if (eq) out[field] = eq;
        } else if (op === 'gt' || op === 'after') {
          if (gte) out[`${field}_gte`] = gte;
        } else if (op === 'lt' || op === 'before') {
          if (lte) out[`${field}_lte`] = lte;
        } else {
          if (gte) out[`${field}_gte`] = gte;
          if (lte) out[`${field}_lte`] = lte;
        }
      }
    }
    return out;
  }, [filterableColumns, filters]);

  const activeFilterCount = Object.keys(filterParams).length;
  // Multi-select column is always rendered so users can select rows on every report;
  // the bulk-actions toolbar only appears when there are actions to apply.
  const showSelectColumn = true;
  const hasRowActions = Boolean(config.rowActions?.length);

  // URL search params — hoisted above the data-fetch block so the
  // passthrough memo / queryKey can react to them. (A second `const` for
  // the same hook lower in the file would be invalid; the deep-link block
  // below now reuses this instance.)
  const [searchParams, setSearchParams] = useSearchParams();

  // Passthroughs — URL search params that should be forwarded verbatim to the
  // API on every fetch. `includeOrphans` is a stable audit flag; the rest are
  // derived from the route's tab strip(s) (each tab declares which keys it
  // touches, and those keys carry over to the server as plain filter params).
  const tabPassthroughKeys = React.useMemo(() => {
    const keys = new Set<string>();
    for (const t of [...(config.tabs ?? []), ...(config.secondaryTabs ?? [])]) {
      for (const k of Object.keys(t.filter ?? {})) keys.add(k);
    }
    return [...keys];
  }, [config.tabs, config.secondaryTabs]);
  // `location` is forwarded verbatim so dashboard drill-downs (e.g. the Service
  // Reminders card) can scope a report by service location — the backend route
  // resolves it (e.g. /upcoming-services maps it to asset $in). Endpoints that
  // don't understand it simply ignore it.
  const PASSTHROUGH_PARAMS = React.useMemo(
    () => ['includeOrphans', 'location', 'serviceDue', ...tabPassthroughKeys],
    [tabPassthroughKeys],
  );
  const passthroughKey = React.useMemo(() => {
    const parts: string[] = [];
    for (const k of PASSTHROUGH_PARAMS) {
      const v = searchParams.get(k);
      if (v !== null) parts.push(`${k}=${v}`); // include empty strings for "unset" tabs
    }
    return parts.join('&');
  }, [searchParams, PASSTHROUGH_PARAMS]);

  // Determine the currently-active tab. Resolution order:
  //   1. `to`-based tab whose path matches the current pathname (longest
  //      prefix wins — handles nested routes).
  //   2. `filter`-based tab whose query-string keys all match.
  //   3. Fallback to the first tab with no filter (the "All" entry).
  const location = useLocation();
  // Shared resolver — used for both the primary `tabs` row and the
  // secondary `secondaryTabs` row. Path-based `to:` tabs match against the
  // current pathname (longest-prefix wins); filter-based tabs match when
  // every key in their `filter` is present in the URL.
  const resolveActiveTabIndex = React.useCallback((tabs: TabConfig[]) => {
    if (tabs.length === 0) return -1;
    const pathname = location.pathname;
    let bestPathIdx = -1;
    let bestPathLen = -1;
    for (let i = 0; i < tabs.length; i++) {
      const to = tabs[i].to;
      if (to && pathname.startsWith(to) && to.length > bestPathLen) {
        bestPathIdx = i; bestPathLen = to.length;
      }
    }
    if (bestPathIdx !== -1) return bestPathIdx;
    for (let i = 0; i < tabs.length; i++) {
      const f = tabs[i].filter;
      if (!f || Object.keys(f).length === 0) continue;
      const matches = Object.entries(f).every(([k, v]) => searchParams.get(k) === v);
      if (matches) return i;
    }
    const noFilterIdx = tabs.findIndex(
      (t) => !t.to && (!t.filter || Object.keys(t.filter).length === 0),
    );
    return noFilterIdx === -1 ? 0 : noFilterIdx;
  }, [location.pathname, searchParams]);

  const activeTabIndex          = React.useMemo(() => resolveActiveTabIndex(config.tabs ?? []),          [config.tabs, resolveActiveTabIndex]);
  const activeSecondaryTabIndex = React.useMemo(() => resolveActiveTabIndex(config.secondaryTabs ?? []), [config.secondaryTabs, resolveActiveTabIndex]);

  // Shared click handler — `tabs` argument tells it which row was clicked,
  // and which other-row keys need to be preserved when writing the URL.
  function selectFromTabs(tabs: TabConfig[], idx: number, otherTabs: TabConfig[]) {
    const tab = tabs?.[idx];
    if (!tab) return;
    if (tab.to) { navigate(tab.to); return; }
    const next = new URLSearchParams(searchParams);
    // Clear keys touched by THIS row's tabs only — preserves the active
    // selection on the OTHER row.
    const myKeys = new Set<string>();
    for (const t of tabs) for (const k of Object.keys(t.filter ?? {})) myKeys.add(k);
    for (const k of myKeys) {
      // Don't strip a key the other row also owns.
      const otherOwns = otherTabs.some((t) => t.filter && Object.prototype.hasOwnProperty.call(t.filter, k));
      if (!otherOwns) next.delete(k);
    }
    for (const [k, v] of Object.entries(tab.filter ?? {})) next.set(k, v);
    setSearchParams(next, { replace: true });
  }
  function selectTab(idx: number)          { selectFromTabs(config.tabs ?? [],          idx, config.secondaryTabs ?? []); }
  function selectSecondaryTab(idx: number) { selectFromTabs(config.secondaryTabs ?? [], idx, config.tabs ?? []); }

  // Build the server query string for the current search / filters / sort /
  // passthrough state. Shared by the paged table query and the Export / Print
  // handlers (which fetch every matching row, not just the visible page).
  const buildListParams = React.useCallback((opts: { page: number; limit: number }) => {
    const params = new URLSearchParams({ page: String(opts.page), limit: String(opts.limit) });
    if (search) params.set('search', search);
    // Multi-chip text filters store values `\n`-separated in a single
    // state slot. Expand them into repeated query params so the server
    // sees `?field_contains=a&field_contains=b&…` and ORs them.
    for (const [k, v] of Object.entries(filterParams)) {
      if (v.includes('\n')) {
        for (const part of v.split('\n')) {
          const t = part.trim();
          if (t) params.append(k, t);
        }
      } else {
        params.set(k, v);
      }
    }
    if (sortState) {
      params.set('sort', sortState.field);
      params.set('order', sortState.order);
    }
    for (const k of PASSTHROUGH_PARAMS) {
      const v = searchParams.get(k);
      if (v) params.set(k, v);
    }
    return params;
  }, [search, filterParams, sortState, PASSTHROUGH_PARAMS, searchParams, config.endpoint]);

  const { data, isLoading } = useQuery({
    queryKey: [config.endpoint, search, page, filterParams, sortState, passthroughKey],
    queryFn: async () => {
      const params = buildListParams({ page, limit: 25 });
      const r = await api<any[]>(`${config.endpoint}?${params.toString()}`);
      return { items: r.data || [], meta: r.meta };
    },
  });

  // ---- Export / Print -----------------------------------------------------
  // The server generates the file from EVERY row matching the active search /
  // filters / sort (not just the 25 on screen). We send it the columns we
  // currently show; it streams back a CSV or PDF. `buildListParams` provides
  // the same filter query string the list uses; page/limit are ignored server-
  // side for exports.
  // `reportBusy` holds the in-flight format ('csv' | 'json' | … | 'pdf') or
  // 'print'; null when idle. Used to disable both controls and label them.
  const [reportBusy, setReportBusy] = React.useState<null | ExportFileFormat | 'print'>(null);
  const exporting = reportBusy !== null && reportBusy !== 'print';
  async function handleExport(format: ExportFileFormat) {
    if (reportBusy) return;
    setReportBusy(format);
    try {
      const columns = buildExportColumns(visibleColumns, format);
      const params = buildListParams({ page: 1, limit: 1 });
      const res = await apiBlob(`${config.endpoint}/export?${params.toString()}`, {
        method: 'POST',
        body: JSON.stringify({ columns, format, title: config.title }),
      });
      const stamp = toDateInputValue(new Date());
      // The export endpoint uses the format string as the file extension.
      downloadBlob(await res.blob(), `${fileSlug(config.title)}-${stamp}.${format}`);
    } catch (e: any) {
      toast('Export failed', e?.message ?? String(e), 'error');
    } finally {
      setReportBusy(null);
    }
  }
  async function handlePrint() {
    if (reportBusy) return;
    setReportBusy('print');
    try {
      const columns = buildExportColumns(visibleColumns, 'pdf');
      const params = buildListParams({ page: 1, limit: 1 });
      const res = await apiBlob(`${config.endpoint}/export?${params.toString()}`, {
        method: 'POST',
        body: JSON.stringify({ columns, format: 'pdf', title: config.title }),
      });
      // Load the server PDF into a hidden iframe and open the print dialog.
      printPdfBlob(await res.blob());
    } catch (e: any) {
      toast('Print failed', e?.message ?? String(e), 'error');
    } finally {
      setReportBusy(null);
    }
  }

  // Reset to page 1 whenever the filter set changes — otherwise the user can
  // be stranded on page 4 of an empty result.
  const filterFingerprint = React.useMemo(() => JSON.stringify(filterParams), [filterParams]);
  React.useEffect(() => { setPage(1); }, [filterFingerprint]);

  // Safety net: if the response says we're past the last page (e.g. records
  // shrank, scope flipped to 'own', persisted page state stale), snap back
  // to page 1 instead of showing "No records" on a phantom page.
  React.useEffect(() => {
    if (data?.meta && page > Math.max(1, data.meta.totalPages) && !isLoading) {
      setPage(1);
    }
  }, [data?.meta, page, isLoading]);

  const [toolbarPending, setToolbarPending] = React.useState<string | null>(null);
  const [toolbarDialog, setToolbarDialog] = React.useState<ToolbarActionConfig | null>(null);
  // Compact header toolbar — driven by the actual row width, not viewport.
  // The detail panel can shrink the page even on large viewports, so we
  // observe the row directly and flip to icon-only mode when it can't fit
  // the full-label buttons.
  const headerRowRef = React.useRef<HTMLDivElement>(null);
  const [headerCompact, setHeaderCompact] = React.useState(false);
  React.useEffect(() => {
    const el = headerRowRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        // 640px chosen so "Sync EROAD" + "Sync Blackhawk" + "+ New" still fit
        // alongside the page title; below this we collapse to icons.
        setHeaderCompact(e.contentRect.width < 640);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const runToolbarAction = React.useCallback(async (action: ToolbarActionConfig) => {
    if (action.confirm && !(await confirm({ title: action.confirm, destructive: action.variant === 'destructive' }))) return;
    if (action.customDialog) {
      setToolbarDialog(action);
      return;
    }
    setToolbarPending(action.label);
    try {
      if (action.onClick) {
        await action.onClick({
          refetch: () => qc.invalidateQueries({ queryKey: [config.endpoint] }),
          navigate,
        });
      } else if (action.endpoint) {
        const r = await api(action.endpoint, {
          method: 'POST',
          body: action.body ? JSON.stringify(action.body) : undefined,
        });
        if (!r.success) throw new Error(r.error?.message || 'Action failed');
        toast(action.label, 'Done', 'success');
        qc.invalidateQueries({ queryKey: [config.endpoint] });
      }
    } catch (e: any) {
      toast('Error', e.message ?? String(e), 'error');
    } finally {
      setToolbarPending(null);
    }
  }, [qc, config.endpoint, navigate]);

  const createMut = useMutation({
    mutationFn: async ({ body, then: follow }: { body: any; then?: NonNullable<CrudConfig['createAndThen']> }) => {
      const r = await api<any>(config.endpoint, { method: 'POST', body: JSON.stringify(body) });
      if (!r.success) throw new Error(r.error?.message || 'Save failed');
      const newId = r.data?.id || r.data?._id;
      if (config.afterSave && newId) {
        await config.afterSave(String(newId), formValues, true);
      }
      // Follow-up transition (Create & Approve). Deliberately inside mutationFn:
      // a failure here surfaces as the mutation error — the record still exists,
      // the dialog stays open and the toast names what went wrong, rather than
      // reporting success for a half-done action.
      if (follow && newId) {
        const r2 = await api<any>(follow.endpoint.replace(':id', String(newId)), { method: 'POST' });
        if (!r2.success) {
          throw new Error(`${config.singular} created, but ${follow.label} failed: ${r2.error?.message || 'unknown error'}`);
        }
        return { ...r.data, __followed: true };
      }
      return r.data;
    },
    onSuccess: (data: any) => {
      toast(data?.__followed ? `${config.singular} created and ${config.createAndThen?.label ?? 'actioned'}` : `${config.singular} created`, undefined, 'success');
      qc.invalidateQueries({ queryKey: [config.endpoint] });
      setDialogOpen(false);
    },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  const updateMut = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: any }) => {
      const r = await api<any>(`${config.endpoint}/${id}`, { method: 'PUT', body: JSON.stringify(body) });
      if (!r.success) throw new Error(r.error?.message || 'Save failed');
      if (config.afterSave) {
        await config.afterSave(id, formValues, false);
      }
      return r.data;
    },
    onSuccess: () => {
      toast(`${config.singular} updated`, undefined, 'success');
      qc.invalidateQueries({ queryKey: [config.endpoint] });
      setDialogOpen(false);
    },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const r = await api(`${config.endpoint}/${id}`, { method: 'DELETE' });
      if (!r.success) throw new Error(r.error?.message || 'Delete failed');
    },
    onSuccess: () => {
      toast(`${config.singular} deleted`, undefined, 'success');
      qc.invalidateQueries({ queryKey: [config.endpoint] });
    },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  const bulkMut = useMutation({
    mutationFn: async ({ action, ids }: { action: BulkActionConfig; ids: string[] }) => {
      if (!action.endpoint) throw new Error(`Bulk action "${action.label}" has no endpoint`);
      const body: Record<string, unknown> = { [action.idsKey ?? 'ids']: ids };
      if (action.promptReason) {
        const reason = window.prompt('Reason?') ?? '';
        body.reason = reason;
      }
      const r = await api(action.endpoint, { method: 'POST', body: JSON.stringify(body) });
      if (!r.success) throw new Error(r.error?.message || 'Action failed');
      return r.data;
    },
    onSuccess: (data: any, vars) => {
      toast(vars.action.label, JSON.stringify(data ?? {}), 'success');
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: [config.endpoint] });
    },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  // Bridge for bulk actions that ship a custom `onClick` instead of a
  // direct `endpoint` — lets the host page open its own picker / confirm
  // / multi-step flow with the selected ids.
  const runBulkOnClick = React.useCallback(
    async (action: BulkActionConfig, ids: string[]) => {
      if (!action.onClick) return;
      await action.onClick(ids, {
        refetch: () => qc.invalidateQueries({ queryKey: [config.endpoint] }),
        navigate,
        clearSelection: () => setSelected(new Set()),
      });
    },
    [qc, config.endpoint, navigate],
  );

  // Built-in bulk edit / delete — talk to the CRUD factory's PATCH /bulk
  // and DELETE /bulk routes. Gated by the same `edit` / `delete`
  // permissions used for the row-level Edit and Delete buttons, so a user
  // who can edit one record can edit many at once.
  const [bulkEditOpen, setBulkEditOpen] = React.useState(false);
  const bulkEditMut = useMutation({
    mutationFn: async ({ ids, patch }: { ids: string[]; patch: Record<string, any> }) => {
      const r = await api(`${config.endpoint}/bulk`, {
        method: 'PATCH',
        body: JSON.stringify({ ids, patch }),
      });
      if (!r.success) throw new Error(r.error?.message || 'Bulk edit failed');
      return r.data as { matched: number; modified: number };
    },
    onSuccess: (data) => {
      toast('Bulk edit complete', `${data?.modified ?? 0} record(s) updated`, 'success');
      setSelected(new Set());
      setBulkEditOpen(false);
      qc.invalidateQueries({ queryKey: [config.endpoint] });
    },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });
  const bulkDeleteMut = useMutation({
    mutationFn: async ({ ids }: { ids: string[] }) => {
      const r = await api(`${config.endpoint}/bulk`, {
        method: 'DELETE',
        body: JSON.stringify({ ids }),
      });
      if (!r.success) throw new Error(r.error?.message || 'Bulk delete failed');
      return r.data as { deleted: number; skipped?: number; skippedReason?: string };
    },
    onSuccess: (data) => {
      const skipped = data?.skipped ?? 0;
      toast(
        'Bulk delete complete',
        `${data?.deleted ?? 0} record(s) deleted`
          + (skipped ? `; ${skipped} skipped${data?.skippedReason ? ` (e.g. ${data.skippedReason})` : ''}` : ''),
        skipped ? 'error' : 'success',
      );
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: [config.endpoint] });
    },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  // Built-in bulk actions injected for every report — gated by the same
  // edit/delete CRUD permissions as their per-row counterparts. Sit BEFORE
  // the configured ones so admins always see Edit/Delete at the top of the
  // Actions menu. Composed here (after the mutations are declared) so the
  // built-in handlers can capture them.
  const effectiveBulkActions = React.useMemo<BulkActionConfig[]>(() => {
    const builtins: BulkActionConfig[] = [];
    if (canEdit && !config.disableBulkEdit) {
      builtins.push({
        label: 'Edit Selected',
        onClick: () => setBulkEditOpen(true),
      });
    }
    if (canRemove && !config.disableBulkDelete && config.canDelete !== false) {
      builtins.push({
        label: 'Delete Selected',
        variant: 'destructive',
        confirm: `Delete ${selected.size} record(s)? This cannot be undone.`,
        onClick: (ids) => bulkDeleteMut.mutate({ ids }),
      });
    }
    // `enabledFor` drops an action when no selected row can take it. Only the
    // current page's rows are resolvable (selection survives paging), so a
    // selection containing anything we cannot see keeps the action visible —
    // see the note on the field.
    const byId = new Map<string, any>((data?.items ?? []).map((r: any) => [String(r.id), r]));
    const selectedRows = [...selected].map((id) => byId.get(String(id)));
    const allResolved = selectedRows.every(Boolean);
    const applicable = (a: BulkActionConfig) => {
      if (!a.enabledFor || selected.size === 0 || !allResolved) return true;
      return selectedRows.some((r) => a.enabledFor!(r));
    };
    return [...builtins, ...visibleBulkActions.filter(applicable)];
    // selected.size feeds the confirm string — primitive, so re-memoising
    // on size changes is fine. `selected` itself is needed for `enabledFor`.
  }, [canEdit, canRemove, config.disableBulkEdit, config.disableBulkDelete, config.canDelete, visibleBulkActions, selected, selected.size, bulkDeleteMut, data?.items]);
  const hasBulk = effectiveBulkActions.length > 0;

  // When an action needs the user to pick a ref value (e.g. change service type),
  // we stash it here and the <PickDialog> fires the mutation once the user confirms.
  const [pickDialog, setPickDialog] = React.useState<{ action: RowActionConfig; row: any } | null>(null);
  // Same pattern for actions that ship a `customDialog` — the rendered
  // component receives the row + close callback. Used by Offboard Asset
  // and any future form-style row action.
  const [customDialog, setCustomDialog] = React.useState<{ action: RowActionConfig; row: any } | null>(null);

  const rowActionMut = useMutation({
    mutationFn: async ({ action, row, extraBody }: { action: RowActionConfig; row: any; extraBody?: Record<string, unknown> }) => {
      if (!action.endpoint) throw new Error(`Action "${action.label}" has no endpoint`);
      const url = action.endpoint.replace(':id', row.id);
      const body: Record<string, unknown> = { ...(action.body || {}), ...(extraBody || {}) };
      if (action.promptReason) {
        const reason = window.prompt('Reason?') ?? '';
        body.reason = reason;
      }
      const r = await api(url, {
        method: action.method ?? 'POST',
        body: Object.keys(body).length ? JSON.stringify(body) : undefined,
      });
      if (!r.success) throw new Error(r.error?.message || 'Action failed');
      return r.data;
    },
    onSuccess: (_data, vars) => {
      toast(vars.action.label, `${config.singular} updated`, 'success');
      qc.invalidateQueries({ queryKey: [config.endpoint] });
      setDialogOpen(false);
      setPickDialog(null);
    },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  async function triggerRowAction(action: RowActionConfig, row: any) {
    // Blocked-precondition check FIRST — before the link/confirm/pick branches,
    // so no variant of an action can slip past it. Explains and offers a way
    // forward instead of letting the request fail server-side.
    const block = action.blocked?.(row) ?? null;
    if (block) {
      const go = await confirm({
        title: block.title ?? `${action.label} unavailable`,
        description: block.message,
        confirmLabel: block.goTo ? (block.goToLabel ?? 'Go there') : 'OK',
        cancelLabel: 'Close',
      });
      if (go && block.goTo) {
        navigate(block.goTo, { state: { from: window.location.pathname + window.location.search } });
      }
      return;
    }
    if (action.link) {
      const path = typeof action.link === 'function' ? action.link(row) : action.link;
      // Carry the current URL through router state so the destination's
      // Back button can return to this report's exact view (filter +
      // pagination preserved via the querystring).
      navigate(path, { state: { from: window.location.pathname + window.location.search } });
      return;
    }
    if (action.confirm && !(await confirm({ title: action.confirm, destructive: action.variant === 'destructive' }))) return;
    if (action.pick) {
      setPickDialog({ action, row });
      return;
    }
    if (action.customDialog) {
      setCustomDialog({ action, row });
      return;
    }
    if (action.onClick) {
      await action.onClick(row, {
        refetch: () => qc.invalidateQueries({ queryKey: [config.endpoint] }),
        navigate,
      });
      return;
    }
    rowActionMut.mutate({ action, row });
  }

  // Build dependency map once: parentField → [childField, …] from `dependsOn.formField`.
  const dependentsByParent = React.useMemo(() => {
    const map = new Map<string, string[]>();
    for (const f of config.fields) {
      if (f.dependsOn?.formField) {
        const arr = map.get(f.dependsOn.formField) ?? [];
        arr.push(f.name);
        map.set(f.dependsOn.formField, arr);
      }
    }
    return map;
  }, [config.fields]);

  // When a parent ref's value changes (especially → null), cascade-clear all
  // descendants so stale child selections never round-trip to the API.
  function handleFieldChange(name: string, value: any) {
    setFormValues((prev) => {
      // `set` is immutable (returns a new object), so each clear must be
      // re-captured into `next` — otherwise the descendant resets are lost and
      // a stale child (e.g. servicePlan after changing asset) lingers.
      let next = set(prev, name, value);
      const stack = [name];
      const cleared = new Set<string>();
      while (stack.length) {
        const parent = stack.pop()!;
        for (const child of dependentsByParent.get(parent) ?? []) {
          if (cleared.has(child)) continue;
          cleared.add(child);
          next = set(next, child, null);
          stack.push(child);
        }
      }
      return next;
    });
  }

  /** The inline edit/new form renders as a Card BELOW the table on the same
   *  page. If the user opens the form after scrolling down to row 21, the
   *  page stays scrolled to that row — leaving them halfway down the form
   *  and unsure where it starts. Scroll the window back to the top so the
   *  form's first field is always the first thing they see. */
  function scrollFormIntoView() {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }
  function openNew() {
    // createOverride hook: when the page owns its own create Dialog
    // (e.g. EmployeesPage's complex multi-field form), short-circuit the
    // built-in inline form entirely.
    if (config.createOverride) { config.createOverride(); return; }
    setEditing(null);
    setFormValues(config.defaultValues || {});
    setDialogOpen(true);
    scrollFormIntoView();
  }
  function openEdit(row: any) {
    // editOverride hook: same purpose as createOverride but for the
    // pencil / context-menu / detail-panel Edit triggers. Checked first
    // so a page can keep its custom edit Dialog without writing a route.
    if (config.editOverride) { config.editOverride(row); return; }
    // editLink hook: when the config wants the Edit action to deep-link
    // out to a dedicated page (e.g. ServiceHistory → JobCard service-
    // execution page), short-circuit the inline form entirely. We stamp
    // the current URL into router state so the destination page's Back
    // button can return the operator to this exact report (location + the
    // active filter querystring) instead of a hard-coded fallback.
    if (config.editLink) {
      const url = config.editLink(row);
      if (url) {
        navigate(url, { state: { from: window.location.pathname + window.location.search } });
        return;
      }
    }
    setViewing(null);
    setEditing(row);
    setFormValues(row);
    setDialogOpen(true);
    scrollFormIntoView();
  }
  function openDetail(row: any) {
    setViewing(row);
  }
  /** Default row-click handler. Honours the config flags:
   *   - `clickRowToEdit: true`  → legacy "go straight to edit" behaviour
   *   - `clickRowToView: false` → click is a no-op (rare)
   *   - default → open the read-only DetailDialog (with Edit button for
   *               users who have edit permission)
   *  Returns null when nothing should happen on click so callers can attach
   *  it conditionally and avoid an unnecessary hover state.
   */
  function rowClickHandler(row: any): (() => void) | undefined {
    if (config.clickRowToEdit) return canEdit ? () => openEdit(row) : undefined;
    if (config.clickRowToView === false) return undefined;
    return () => openDetail(row);
  }
  async function confirmAndDelete(row: any, onDone?: () => void) {
    if (await confirm({ title: `Delete this ${config.singular.toLowerCase()}?`, destructive: true, confirmLabel: 'Delete' })) {
      deleteMut.mutate(row.id, { onSuccess: onDone });
    }
  }
  /** onContextMenu handler factory — captures the row + mouse coords. */
  function onRowContextMenu(row: any): (e: React.MouseEvent) => void {
    return (e) => {
      e.preventDefault();
      setCtxMenu({ row, x: e.clientX, y: e.clientY });
    };
  }

  // Deep-link: if the URL carries `?edit=<id>`, fetch that record by id
  // and open it in the inline edit form. Used by the Service Report
  // dashboard (and any other report that wants to "click to open
  // details") to land the user straight on the asset detail. The query
  // param is cleared after we've consumed it so a subsequent close
  // doesn't immediately re-open. `searchParams` itself is declared once
  // above; only consumer reads happen here.
  const editIdFromUrl = searchParams.get('edit');
  const viewIdFromUrl = searchParams.get('view');

  // ---- URL-driven filter seeding -----------------------------------------
  // Allow other pages (e.g. the Service Report dashboard) to deep-link into
  // a CrudPage report with pre-applied filters via plain URL params, e.g.
  //   /servicing/history?asset=<id>&servicePlanSchedule=<id>
  // For each query param that matches one of this report's filterable
  // columns, write the value into the user's persisted filter state and
  // strip the param from the URL so refresh + close-reopen behave normally.
  //
  // ⚠️ TIMING. This MUST wait for `filtersReady`. `usePreferences` starts at
  // the default `{}` and fetches the saved filters over the network; seeding
  // before that response lands looked like it worked and was then silently
  // overwritten by the saved value. Symptom: clicking a cell on the Service
  // Report dashboard opened Service History still showing whatever the user
  // last filtered it to (fleet 67), never the asset they clicked. The params
  // had already been stripped from the URL, so nothing could re-apply them.
  //
  // ⚠️ REPLACE, don't merge. A deep link means "show me exactly these rows".
  // Merging left the user's other saved filters in force — a stale date range
  // or status could still hide the very rows the link was meant to reveal.
  // Cost, accepted: drilling in from the dashboard discards that report's
  // saved filters.
  const seededFromUrlRef = React.useRef(false);
  React.useEffect(() => {
    if (!filtersReady || seededFromUrlRef.current) return;
    const additions: Record<string, string> = {};
    const consumed: string[] = [];
    for (const c of (config.columns as any[])) {
      if (!c.filterType) continue;
      const field = c.filterField || c.key;
      if (c.filterType === 'text') {
        const v = searchParams.get(field) ?? searchParams.get(`${field}_contains`);
        if (v) {
          additions[`${field}_contains`] = v;
          consumed.push(field, `${field}_contains`);
        }
      } else if (c.filterType === 'enum' || c.filterType === 'boolean' || c.filterType === 'ref') {
        const v = searchParams.get(field);
        if (v) { additions[field] = v; consumed.push(field); }
      } else if (c.filterType === 'date' || c.filterType === 'number') {
        const gte = searchParams.get(`${field}_gte`);
        const lte = searchParams.get(`${field}_lte`);
        if (gte) { additions[`${field}_gte`] = gte; consumed.push(`${field}_gte`); }
        if (lte) { additions[`${field}_lte`] = lte; consumed.push(`${field}_lte`); }
      }
    }
    if (Object.keys(additions).length > 0) {
      seededFromUrlRef.current = true;
      setFilters(additions);
      setPage(1);
      const next = new URLSearchParams(searchParams);
      for (const k of consumed) next.delete(k);
      setSearchParams(next, { replace: true });
    } else if (filtersReady) {
      // No deep-link params on this page load — don't seed again later.
      seededFromUrlRef.current = true;
    }
  // Runs once, on the first render after the saved filters have loaded.
  // Re-seeding after that would fight the user's own filter edits.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersReady]);
  React.useEffect(() => {
    if (!editIdFromUrl && !viewIdFromUrl) return;
    let cancelled = false;
    (async () => {
      try {
        const id = editIdFromUrl ?? viewIdFromUrl!;
        const r = await api<any>(`${config.endpoint}/${id}`);
        if (cancelled) return;
        if (r.success && r.data) {
          if (editIdFromUrl) openEdit(r.data);
          else openDetail(r.data);
        }
      } catch { /* swallow — bad/stale id shouldn't block the page */ }
      // Always clear the param so close+reopen behaves normally.
      const next = new URLSearchParams(searchParams);
      next.delete('edit');
      next.delete('view');
      setSearchParams(next, { replace: true });
    })();
    return () => { cancelled = true; };
  // openEdit/openDetail are stable for the lifetime of the page; intentionally omitted.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editIdFromUrl, viewIdFromUrl, config.endpoint]);
  function submit(follow?: NonNullable<CrudConfig['createAndThen']>) {
    const errs = config.validate?.(formValues) ?? null;
    if (errs && Object.keys(errs).length > 0) return;
    // The local `set` helper (line ~212) returns a new object instead of
    // mutating its first arg. Earlier versions of this loop ignored the
    // return value, so every field-typed-in-the-form ended up dropped from
    // the POST/PUT payload (the request body was always {}). Always rebind
    // `body` from the return.
    let body: any = {};
    for (const f of config.fields) {
      if (f.type === 'custom') continue; // UI-only; writes its keys via extraSaveKeys
      const v = get(formValues, f.name);
      if (v !== undefined && v !== '') {
        body = set(body, f.name, v);
      } else if (editing?.id && v === '' && !f.required) {
        // EDIT only: a field the user cleared must be sent as `null` so the
        // server's *partial* update actually unsets it. Simply dropping it
        // (the create-time behaviour above) leaves the previous value
        // untouched — the "save succeeds but nothing changes / can't clear a
        // field" bug. `null` (not '') so numeric/date fields clear too;
        // required fields are never nulled.
        body = set(body, f.name, null);
      }
    }
    // Pull through extra keys managed by `FormExtras` (e.g. nested
    // sub-documents the basic field renderer doesn't know about).
    for (const key of config.extraSaveKeys ?? []) {
      const v = (formValues as any)[key];
      if (v !== undefined) body[key] = v;
    }
    // Normalize refs stored as populated objects → flatten to ObjectId string(s)
    for (const f of config.fields) {
      if (f.type === 'ref') {
        const id = refToId(get(body, f.name));
        if (id != null) body = set(body, f.name, id);
      } else if (f.type === 'multi-ref') {
        body = set(body, f.name, refsToIds(get(body, f.name)));
      }
    }
    if (editing?.id) updateMut.mutate({ id: editing.id, body });
    else createMut.mutate({ body, then: follow });
  }

  return (
    <div className="space-y-4">
      <div ref={headerRowRef} className="flex items-center justify-between gap-2">
        {dialogOpen ? (
          <div className="flex items-center gap-3 min-w-0">
            <Button size="icon" variant="ghost" onClick={() => setDialogOpen(false)} aria-label="Back to list">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-2xl font-semibold truncate">
              {editing ? `Edit ${config.singular}` : `New ${config.singular}`}
            </h1>
          </div>
        ) : (
          <h1 className="text-2xl font-semibold">{config.title}</h1>
        )}
        {!dialogOpen && (
          // Compact mode (toolbar actions become an icon-only dropdown,
          // "+ New" becomes an icon button) kicks in either on small
          // viewports OR when the row itself is too narrow (e.g. desktop
          // with the detail panel open shrinking the page).
          headerCompact ? (
            <div className="flex items-center gap-2">
              {visibleToolbarActions.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      aria-label="Sync"
                      disabled={toolbarPending !== null}
                      className="w-auto px-2 gap-1"
                    >
                      <RefreshCw className={cn('h-4 w-4', toolbarPending !== null && 'animate-spin')} />
                      <ChevronDown className="h-3 w-3 opacity-70" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {visibleToolbarActions.map((action) => {
                      const lbl = action.dynamicLabel ? action.dynamicLabel() : action.label;
                      return (
                        <DropdownMenuItem
                          key={action.label}
                          disabled={toolbarPending !== null}
                          onClick={() => runToolbarAction(action)}
                        >
                          {toolbarPending === action.label ? `${lbl}…` : lbl}
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {config.canCreate !== false && canAdd && (
                <Button onClick={openNew} size="icon" aria-label={`New ${config.singular}`}>
                  <Plus className="h-4 w-4" />
                </Button>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              {visibleToolbarActions.map((action) => {
                const lbl = action.dynamicLabel ? action.dynamicLabel() : action.label;
                const v = action.dynamicVariant ? action.dynamicVariant() : action.variant;
                return (
                  <Button
                    key={action.label}
                    variant={v ?? 'outline'}
                    disabled={toolbarPending !== null}
                    onClick={() => runToolbarAction(action)}
                  >
                    {toolbarPending === action.label ? `${lbl}…` : lbl}
                  </Button>
                );
              })}
              {config.canCreate !== false && canAdd && (
                <Button onClick={openNew}><Plus className="h-4 w-4" /> New</Button>
              )}
            </div>
          )
        )}
      </div>

      {!dialogOpen && (<>
      {config.tabs && config.tabs.length > 0 && (
        // Primary tab strip — typically lifecycle (Active / Archived / etc.).
        // `overflow-x-auto` + `shrink-0` + `whitespace-nowrap` lets the strip
        // scroll horizontally on narrow viewports instead of clipping tabs
        // off the right edge (Job Cards has 5 tabs that don't fit on mobile).
        // Mobile uses tighter padding + smaller text so most tab sets fit
        // without scrolling; scroll stays available as a fallback.
        <div className="flex items-center gap-0.5 md:gap-1 border-b -mb-px overflow-x-auto scrollbar-hide">
          {config.tabs.map((t, i) => (
            <button
              key={t.label}
              type="button"
              onClick={() => selectTab(i)}
              className={cn(
                'shrink-0 whitespace-nowrap px-2 py-1 text-xs md:px-3 md:py-1.5 md:text-sm font-medium border-b-2 transition-colors -mb-px',
                i === activeTabIndex
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}
      {config.secondaryTabs && config.secondaryTabs.length > 0 && (
        // Secondary tab strip — typically a sub-filter (IoT provider, etc.).
        // Rendered slightly smaller and inside a muted container so it
        // visually defers to the primary lifecycle row above.
        <div className="flex items-center gap-0.5 md:gap-1 text-[11px] md:text-xs overflow-x-auto scrollbar-hide">
          {config.secondaryTabs.map((t, i) => (
            <button
              key={t.label}
              type="button"
              onClick={() => selectSecondaryTab(i)}
              className={cn(
                'shrink-0 whitespace-nowrap px-2 py-0.5 md:px-2.5 md:py-1 rounded-md font-medium transition-colors',
                i === activeSecondaryTabIndex
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent/40',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative max-w-sm flex-1 min-w-[220px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="pl-8" />
        </div>
        {filterableColumns.length > 0 && activeFilterCount > 0 && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setFilters({})}
            className="text-xs"
          >
            Clear filters ({activeFilterCount})
          </Button>
        )}
        {/* One-shot escape hatch — undoes every per-user customization for
            this report (filters, columns, sort, widths) and restores the
            built-in defaults. Visible only when at least one is non-default,
            so it stays out of the way until needed. */}
        {(activeFilterCount > 0
          || sortState != null
          || hasAnyWidth
          || (visibilityReady && visibleKeys != null && (
            visibleKeys.length !== defaultVisibleKeys.length
            || visibleKeys.some((k, i) => k !== defaultVisibleKeys[i])
          ))) && (
          <Button
            size="sm"
            variant="ghost"
            className="text-xs text-muted-foreground"
            onClick={() => {
              setFilters({});
              setVisibleKeys(defaultVisibleKeys);
              setSortState(null);
              setColWidths({});
            }}
            title="Reset filters, columns, sort, and widths to defaults"
          >
            Reset view
          </Button>
        )}
        <div className="flex-1" />
        {/* Open the column-specific search drawer. Same panel that a
            column header's Search icon opens — surfaced as a toolbar
            button so operators can find it without hunting through the
            headers (the drawer already aggregates EVERY filterable
            column, so opening it from here gives the same affordance
            for any column). */}
        <Button
          size="sm"
          variant="outline"
          onClick={() => setSearchDrawerOpen(true)}
          className="hidden md:inline-flex"
          title="Open the per-column search panel"
        >
          <Search className="h-4 w-4 mr-1" />
          Search
        </Button>
        {/* Export the full filtered result set (every matching row, not just
            the 25 visible) using the currently-visible columns. Pick a file
            format; each downloads directly. */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="sm"
              variant="outline"
              disabled={reportBusy !== null}
              title="Export the current view"
            >
              {/* Icon-only on mobile; label + chevron return at md+. Spinner
                  while a file is being generated. */}
              {exporting
                ? <Loader2 className="h-4 w-4 md:mr-1 animate-spin" />
                : <Download className="h-4 w-4 md:mr-1" />}
              <span className="hidden md:inline">{exporting ? 'Exporting…' : 'Export'}</span>
              <ChevronDown className="h-3 w-3 ml-1 opacity-70 hidden md:inline" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {EXPORT_FILE_FORMATS.map((f) => (
              <DropdownMenuItem
                key={f.value}
                disabled={reportBusy !== null}
                onSelect={() => handleExport(f.value)}
              >
                {f.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        {/* Print the full filtered result set as a clean table (opens the
            browser print dialog → printer or Save as PDF). */}
        <Button
          size="sm"
          variant="outline"
          onClick={handlePrint}
          disabled={reportBusy !== null}
          title="Print the current view"
        >
          {/* Icon-only on mobile; label returns at md+. Spinner while the PDF
              is being generated. */}
          {reportBusy === 'print'
            ? <Loader2 className="h-4 w-4 md:mr-1 animate-spin" />
            : <Printer className="h-4 w-4 md:mr-1" />}
          <span className="hidden md:inline">{reportBusy === 'print' ? 'Preparing…' : 'Print'}</span>
        </Button>
        {/* Column visibility — only meaningful for the desktop table; the
            mobile card layout has its own fixed projection (title + 2
            preview fields) so this dropdown is hidden below md. */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="outline" disabled={!visibilityReady} className="hidden md:inline-flex">
              <ColumnsIcon className="h-4 w-4 mr-1" />
              Columns ({visibleColumns.length}/{allColumnKeys.length})
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="max-h-[60vh] overflow-y-auto w-64">
            <div className="px-2 py-1.5 flex items-center justify-between text-xs gap-2">
              <button
                type="button"
                onClick={() => setVisibleKeys(allColumnKeys)}
                className="text-primary hover:underline"
              >Show all</button>
              {hasAnyWidth && (
                <button
                  type="button"
                  onClick={() => setColWidths({})}
                  className="text-muted-foreground hover:underline"
                  title="Reset all column widths to auto"
                >Reset widths</button>
              )}
              <button
                type="button"
                onClick={() => setVisibleKeys(defaultVisibleKeys)}
                className="text-muted-foreground hover:underline"
              >Reset</button>
            </div>
            {config.columns.map((c) => {
              const checked = visibleSet.has(c.key);
              return (
                <DropdownMenuItem
                  key={c.key}
                  // Prevent menu close so users can toggle multiple columns at once.
                  onSelect={(e) => {
                    e.preventDefault();
                    setVisibleKeys((prev) => {
                      const cur = prev ?? defaultVisibleKeys;
                      const set = new Set(cur);
                      if (set.has(c.key)) set.delete(c.key); else set.add(c.key);
                      // Preserve the original column order so the table layout
                      // doesn't reshuffle on toggle.
                      return allColumnKeys.filter((k) => set.has(k));
                    });
                  }}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <Checkbox checked={checked} onCheckedChange={() => { /* handled by onSelect */ }} />
                  <span className="text-sm">{c.label}</span>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {selected.size > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-md border bg-accent/30 text-sm">
          <div className="font-medium">{selected.size} selected</div>
          <div className="flex-1" />
          {hasBulk ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" disabled={bulkMut.isPending || bulkEditMut.isPending || bulkDeleteMut.isPending}>
                  Actions <ChevronDown className="ml-1 h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {effectiveBulkActions.map((action) => (
                  <DropdownMenuItem
                    key={action.label}
                    className={action.variant === 'destructive' ? 'text-destructive' : ''}
                    onSelect={async () => {
                      if (action.confirm && !(await confirm({ title: action.confirm, destructive: action.variant === 'destructive' }))) return;
                      // `onClick` bridge — lets the host page take over with
                      // a picker / multi-step flow; falls through to the
                      // standard endpoint POST when not set.
                      if (action.onClick) {
                        runBulkOnClick(action, [...selected]);
                      } else {
                        bulkMut.mutate({ action, ids: [...selected] });
                      }
                    }}
                  >
                    {action.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <span className="text-xs text-muted-foreground">No bulk actions available for this report.</span>
          )}
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Clear</Button>
        </div>
      )}

      {/* Desktop table — hidden below lg (1024px). Tablets (incl. landscape)
          get the touch-friendly card list below, which fits their width
          without the dense table overflowing its borders. */}
      <Card className="hidden lg:block">
        {/* CardContent owns BOTH axes of scroll. `overflow-x-auto` alone
            implicitly forces overflow-y to behave as auto (per CSS spec),
            so the sticky table header resolves its containing block to
            CardContent — *not* the page. Giving CardContent an explicit
            viewport-based max-height means it actually scrolls vertically
            against, so `sticky top-0` on TableHeader pins the column
            labels at the top of the table while the rows scroll under
            them. The 14rem offset accounts for the page header, title,
            and toolbar above the card. */}
        <CardContent className="p-0 overflow-auto max-h-[calc(100dvh-14rem)]">
          <Table
            wrapperClassName={null}
            style={hasAnyWidth ? { tableLayout: 'fixed', width: 'max-content', minWidth: '100%' } : undefined}
          >
            <TableHeader className="sticky top-0 z-20 bg-card shadow-[0_1px_0_0_hsl(var(--border))]">
              <TableRow>
                {showSelectColumn && (
                  <TableHead className="w-10">
                    <Checkbox
                      aria-label="Select all rows"
                      checked={
                        (data?.items.length ?? 0) > 0 &&
                        data!.items.every((r: any) => selected.has(r.id))
                      }
                      onCheckedChange={(c) => {
                        if (!data?.items) return;
                        setSelected((prev) => {
                          const next = new Set(prev);
                          if (c) data.items.forEach((r: any) => next.add(r.id));
                          else data.items.forEach((r: any) => next.delete(r.id));
                          return next;
                        });
                      }}
                    />
                  </TableHead>
                )}
                {visibleColumns.map((c) => (
                  <TableHead
                    key={c.key}
                    className="p-0 relative"
                    style={(colWidths ?? {})[c.key] ? { width: (colWidths ?? {})[c.key] } : undefined}
                  >
                    <ColumnHeader
                      column={c}
                      field={colFilterField(c)}
                      sortField={colSortField(c)}
                      sort={sortState}
                      filters={filters}
                      onSort={(order, group) => setSortState({ field: colSortField(c), order, group })}
                      onClearSort={() => setSortState(null)}
                      onHide={() => setVisibleKeys((prev) => {
                        const cur = prev ?? defaultVisibleKeys;
                        return allColumnKeys.filter((k) => k !== c.key && cur.includes(k));
                      })}
                      onSearch={() => { setSearchFocusedColumn(c.key); setSearchDrawerOpen(true); }}
                    />
                    <ResizeHandle
                      onResize={(deltaPx, startWidth) => setColumnWidth(c.key, startWidth + deltaPx)}
                    />
                  </TableHead>
                ))}
                <TableHead className="w-24 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(() => {
                const cols = visibleColumns.length + 1 + (showSelectColumn ? 1 : 0);
                if (isLoading) return (
                  <TableRow><TableCell colSpan={cols} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>
                );
                if (data?.items.length === 0) return (
                  <TableRow><TableCell colSpan={cols} className="text-center py-8 text-muted-foreground">No records</TableCell></TableRow>
                );
                return null;
              })()}
              {data?.items.map((row: any, idx: number) => {
                // Insert a subheader row whenever the grouped column's value
                // changes vs. the previous row. Server is sorted by that field
                // already, so consecutive rows share the value within a group.
                let groupHeader: React.ReactNode = null;
                if (sortState?.group && sortState.field) {
                  const cur = get(row, sortState.field);
                  const prev = idx === 0 ? Symbol('first') : get(data!.items[idx - 1], sortState.field);
                  if (cur !== prev) {
                    const cols = visibleColumns.length + 1 + (showSelectColumn ? 1 : 0);
                    const label = cur == null || cur === '' ? '— (empty)' : String(cur);
                    groupHeader = (
                      <TableRow key={`grp-${idx}`} className="bg-muted/40">
                        <TableCell colSpan={cols} className="text-xs font-semibold text-muted-foreground py-1.5">
                          {label}
                        </TableCell>
                      </TableRow>
                    );
                  }
                }
                return (
                  <React.Fragment key={row.id}>
                    {groupHeader}
                {(() => {
                  const onClick = rowClickHandler(row);
                  return (
                <TableRow
                  className={onClick ? 'cursor-pointer hover:bg-accent/40 transition-colors' : undefined}
                  onClick={onClick}
                  onContextMenu={onRowContextMenu(row)}
                >
                  {showSelectColumn && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        aria-label="Select row"
                        checked={selected.has(row.id)}
                        onCheckedChange={(c) => {
                          setSelected((prev) => {
                            const next = new Set(prev);
                            if (c) next.add(row.id);
                            else next.delete(row.id);
                            return next;
                          });
                        }}
                      />
                    </TableCell>
                  )}
                  {visibleColumns.map((c) => (
                    <TableCell key={c.key}>{c.render ? c.render(row) : (get(row, c.key) ?? '—')}</TableCell>
                  ))}
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-1 justify-end">
                      {hasRowActions && config.rowActions!
                        .filter((a) => a.inline && a.icon)
                        .filter((a) => isGranted(a.actionKey))
                        .filter((a) => !a.visible || a.visible(row))
                        .map((a) => (
                          <Button
                            key={a.label}
                            aria-label={a.label}
                            title={a.label}
                            size="icon"
                            variant="ghost"
                            onClick={() => triggerRowAction(a, row)}
                            disabled={rowActionMut.isPending}
                            className={a.variant === 'destructive' ? 'text-destructive' : ''}
                          >
                            {a.icon}
                          </Button>
                        ))}
                      {/* Overflow for every granted+visible action that is NOT
                          inline. Without this such an action is reachable only
                          by right-clicking the row or opening the detail dialog
                          — discoverable if you already know, invisible if you
                          don't, and on a touch device there is no right-click
                          at all, so a tablet had no route to it. Job cards are
                          the case that exposed it: Assign, Approve, Reject and
                          both Reverts are all non-inline, so the row offered
                          nothing but Edit and Delete. */}
                      {hasRowActions && (() => {
                        const overflow = config.rowActions!
                          .filter((a) => !(a.inline && a.icon))
                          .filter((a) => isGranted(a.actionKey))
                          .filter((a) => !a.visible || a.visible(row));
                        if (overflow.length === 0) return null;
                        return (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                aria-label={`More actions for this ${config.singular.toLowerCase()}`}
                                title="More actions"
                                size="icon"
                                variant="ghost"
                                disabled={rowActionMut.isPending}
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {overflow.map((a) => (
                                <DropdownMenuItem
                                  key={a.label}
                                  className={a.variant === 'destructive' ? 'text-destructive' : ''}
                                  onSelect={() => triggerRowAction(a, row)}
                                >
                                  {a.label}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        );
                      })()}
                      {canEditRow(row) && (
                        <Button aria-label={`Edit ${config.singular.toLowerCase()}`} size="icon" variant="ghost" onClick={() => openEdit(row)}><Pencil className="h-4 w-4" /></Button>
                      )}
                      {canDeleteRow(row) && (
                        <Button aria-label={`Delete ${config.singular.toLowerCase()}`} size="icon" variant="ghost" onClick={async () => {
                          if (await confirm({ title: `Delete this ${config.singular.toLowerCase()}?`, destructive: true, confirmLabel: 'Delete' })) {
                            deleteMut.mutate(row.id);
                          }
                        }}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
                  );
                })()}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Mobile card list — visible below md (768px). Compact: first column
          as the card title, next ~2 columns as a brief preview. Tap the
          card to open the edit dialog (which acts as the full detail view).
          Bulk-select checkbox still works via stopPropagation. */}
      <div className="lg:hidden space-y-2">
        {isLoading && (
          <div className="text-center py-8 text-sm text-muted-foreground">Loading…</div>
        )}
        {!isLoading && data?.items.length === 0 && (
          <div className="text-center py-8 text-sm text-muted-foreground">No records</div>
        )}
        {data?.items.map((row: any, idx: number) => {
          let groupHeader: React.ReactNode = null;
          if (sortState?.group && sortState.field) {
            const cur = get(row, sortState.field);
            const prev = idx === 0 ? Symbol('first') : get(data!.items[idx - 1], sortState.field);
            if (cur !== prev) {
              const label = cur == null || cur === '' ? '— (empty)' : String(cur);
              groupHeader = (
                <div key={`grp-${idx}`} className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-3 pb-1 px-1">
                  {label}
                </div>
              );
            }
          }
          // Card body shows: 1 title column + at most 2 preview columns. Any
          // other fields the user wants to inspect live in the edit dialog.
          const [titleCol, ...rest] = visibleColumns;
          const previewCols = rest.slice(0, 2);
          const renderCell = (col: ColumnConfig) => col.render ? col.render(row) : (get(row, col.key) ?? '—');
          const cardClick = rowClickHandler(row);
          const tappable = !!cardClick;
          // Build the swipe drawer's actions. For now we expose only
          // Delete (the most common destructive op); other row actions
          // remain reachable via the edit dialog. Easy to extend later
          // if a config wants to project specific `rowActions` here.
          const swipeActions: Array<{ label: string; icon: React.ReactNode; variant: 'destructive'; onClick: () => Promise<void> }> = [];
          if (canDeleteRow(row)) {
            swipeActions.push({
              label: 'Delete',
              icon: <Trash2 className="h-5 w-5" />,
              variant: 'destructive',
              onClick: async () => {
                if (await confirm({ title: `Delete this ${config.singular.toLowerCase()}?`, destructive: true, confirmLabel: 'Delete' })) {
                  deleteMut.mutate(row.id);
                }
              },
            });
          }

          const cardBody = (
            <Card
              className={cn(
                'overflow-hidden rounded-none border-x-0 first:border-t-0 last:border-b-0',
                tappable && 'cursor-pointer active:bg-accent/40 transition-colors',
              )}
              onClick={cardClick}
              onContextMenu={onRowContextMenu(row)}
            >
              <CardContent className="p-3 flex items-center gap-3">
                {showSelectColumn && (
                  <Checkbox
                    checked={selected.has(row.id)}
                    onClick={(e) => e.stopPropagation()}
                    onCheckedChange={(c) => {
                      setSelected((prev) => {
                        const next = new Set(prev);
                        if (c) next.add(row.id); else next.delete(row.id);
                        return next;
                      });
                    }}
                  />
                )}
                <div className="flex-1 min-w-0 space-y-0.5">
                  {titleCol && (
                    <div className="text-sm font-medium truncate">{renderCell(titleCol)}</div>
                  )}
                  {previewCols.length > 0 && (
                    <div className="text-xs text-muted-foreground truncate flex flex-wrap gap-x-3">
                      {previewCols.map((c) => (
                        <span key={c.key} className="truncate">
                          <span className="opacity-60">{c.label}: </span>
                          <span className="text-foreground/80">{renderCell(c)}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {tappable && (
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden />
                )}
              </CardContent>
            </Card>
          );

          return (
            <React.Fragment key={row.id}>
              {groupHeader}
              {/* iOS-style swipe-left to reveal Delete. On desktop the
                  same row already has a trash button in the table, so
                  this only kicks in for the `<lg` card list. The
                  component falls through to plain rendering when no
                  actions are available (no delete permission). */}
              {swipeActions.length > 0 ? (
                <SwipeToReveal actions={swipeActions} className="rounded-md border bg-card">
                  {cardBody}
                </SwipeToReveal>
              ) : (
                cardBody
              )}
            </React.Fragment>
          );
        })}
      </div>

      {data?.meta && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <div>{data.meta.total} records · page {data.meta.page} / {data.meta.totalPages}</div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
            <Button size="sm" variant="outline" disabled={page >= (data.meta.totalPages || 1)} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      )}
      </>)}

      {dialogOpen && (
        <Card>
          <CardContent className="p-4 sm:p-6 space-y-6">
            <div className="space-y-6">
              {(() => {
                // Group fields by section (preserving declaration order). When
                // no field declares a section, render the original flat list.
                // Filter out fields whose conditional-visibility predicate
                // says they shouldn't render for the current form values.
                // Done once at the top so both render paths (flat + sectioned)
                // and the section-grouping below agree on what's visible.
                const visibleFields = config.fields.filter(
                  (f) => !f.visibleIf || f.visibleIf(formValues),
                );
                const fieldErrors = config.validate?.(formValues) ?? {};
                const renderError = (name: string) => fieldErrors[name]
                  ? <p className="text-[11px] text-destructive mt-0.5">{fieldErrors[name]}</p>
                  : null;
                // Lock helper: a locked field renders inside a
                // <fieldset disabled> for non-admins so the value stays
                // visible but the inputs are inert. Native fieldset cascades
                // `disabled` to every form control inside it, so this works
                // even for the nested Select / multi-ref / upload widgets
                // without each needing a `disabled` prop.
                const renderField = (f: FieldConfig) => {
                  if (f.type === 'custom') {
                    return f.render?.({ formValues, setField: handleFieldChange, isEdit: !!editing }) ?? null;
                  }
                  const node = (
                    <Field
                      f={f}
                      value={get(formValues, f.name)}
                      onChange={(v) => handleFieldChange(f.name, v)}
                      formValues={formValues}
                      invalid={!!fieldErrors[f.name]}
                    />
                  );
                  if (f.unlockAction && !isGranted(f.unlockAction)) {
                    return (
                      <fieldset disabled className="opacity-70 cursor-not-allowed border-0 p-0 m-0">
                        {node}
                        <p className="text-xs text-muted-foreground italic mt-1">
                          {/* Name the missing grant. "Admin only" told people
                              nothing actionable; a role permission they can
                              ask for by name does. */}
                          Locked — your role doesn&rsquo;t grant &ldquo;
                          {actionByKey(f.unlockAction)?.label ?? f.unlockAction}&rdquo;.
                        </p>
                      </fieldset>
                    );
                  }
                  return node;
                };
                const hasSections = visibleFields.some((f) => f.section);
                if (!hasSections) {
                  return (
                    <div className="space-y-4">
                      {visibleFields.map((f) => (
                        <div key={f.name} className={cn(f.type === 'boolean' ? '' : 'space-y-1.5')} data-field={f.name}>
                          {f.type !== 'boolean' && f.type !== 'custom' && <Label>{f.label}{f.required && <span className="text-destructive"> *</span>}</Label>}
                          {renderField(f)}
                          {renderError(f.name)}
                        </div>
                      ))}
                    </div>
                  );
                }
                const groups: Array<{ name: string | null; fields: FieldConfig[] }> = [];
                for (const f of visibleFields) {
                  const name = f.section ?? null;
                  const last = groups[groups.length - 1];
                  if (!last || last.name !== name) groups.push({ name, fields: [f] });
                  else last.fields.push(f);
                }
                return groups.map((g, i) => (
                  <section key={g.name ?? `__unsec_${i}`} className="space-y-4">
                    {g.name && (
                      <div className="border-b pb-2">
                        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{g.name}</h3>
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                      {g.fields.map((f) => (
                        <div
                          key={f.name}
                          className={cn(
                            f.type === 'boolean' ? '' : 'space-y-1.5',
                            // Wide field types fill the row even on md+.
                            (f.type === 'textarea' || f.type === 'address' || f.type === 'address-search' || f.type === 'image' || f.type === 'file' || f.type === 'custom') && 'md:col-span-2',
                          )}
                          data-field={f.name}
                        >
                          {f.type !== 'boolean' && f.type !== 'custom' && <Label>{f.label}{f.required && <span className="text-destructive"> *</span>}</Label>}
                          {renderField(f)}
                          {renderError(f.name)}
                        </div>
                      ))}
                    </div>
                  </section>
                ));
              })()}
            </div>
            {config.FormExtras && (
              <config.FormExtras
                isEdit={!!editing?.id}
                parentId={editing?.id}
                formValues={formValues}
                setFormValues={setFormValues}
              />
            )}
            {/* Related lists default to "View only" — keeping the edit form
                focused on the parent record's own fields so users aren't
                scrolling through long history sections while editing.
                Resources can opt back in via `showRelatedListsInEdit: true`
                (e.g. Service Plan → schedules need to be inline). */}
            {editing?.id && config.showRelatedListsInEdit && config.relatedLists?.filter((rl) => !rl.hideInEdit).map((rl) => (
              <RelatedList key={rl.endpoint} config={rl} parentId={editing.id} />
            ))}
            <div className="flex flex-wrap gap-2 pt-4 border-t">
              {editing?.id && hasRowActions && (() => {
                const actions = config.rowActions!
                  .filter((a) => isGranted(a.actionKey))
                  .filter((a) => !a.visible || a.visible(editing));
                if (actions.length === 0) return null;
                return (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="secondary" disabled={rowActionMut.isPending}>
                        Actions <ChevronDown className="ml-1 h-3.5 w-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      {actions.map((action) => (
                        <DropdownMenuItem
                          key={action.label}
                          className={action.variant === 'destructive' ? 'text-destructive' : ''}
                          onSelect={() => triggerRowAction(action, editing)}
                        >
                          {action.label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              })()}
              <div className="flex-1" />
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              {/* Create + follow-up transition in one click (e.g. Create & Approve
                  on a job card). Create-only, and only for a holder of the
                  action key — the server enforces the same permission, so this
                  cannot offer a transition it would refuse. */}
              {(() => {
                const cat = config.createAndThen;
                if (editing || !cat || !isGranted(cat.actionKey)) return null;
                const blocked = cat.blockedReason?.(formValues) ?? null;
                const invalid = Object.keys(config.validate?.(formValues) ?? {}).length > 0;
                return (
                  <Button
                    variant="outline"
                    title={blocked ?? undefined}
                    disabled={createMut.isPending || updateMut.isPending || invalid || !!blocked}
                    onClick={async () => {
                      // The app's own modal, not window.confirm: the native one
                      // is unstyled, ignores the platform skin, and on mobile
                      // renders as a system sheet that looks nothing like the
                      // rest of the dialog it was launched from. `confirm` is
                      // already wired up in this component (used by the row
                      // actions) and resolves false on cancel / Escape /
                      // click-outside.
                      if (cat.confirm && !(await confirm({
                        title: cat.confirmTitle ?? `${cat.label}?`,
                        description: cat.confirm,
                        confirmLabel: cat.label,
                      }))) return;
                      submit(cat);
                    }}
                  >
                    {createMut.isPending ? (cat.pendingLabel ?? `${cat.label}…`) : cat.label}
                  </Button>
                );
              })()}
              <Button
                onClick={() => submit()}
                disabled={
                  createMut.isPending || updateMut.isPending ||
                  Object.keys(config.validate?.(formValues) ?? {}).length > 0
                }
              >
                {editing ? 'Update' : 'Create'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {searchDrawerOpen && (
        <SearchDrawer
          columns={config.columns}
          filters={filters}
          setFilters={setFilters}
          fieldFor={colFilterField}
          focusedColumnKey={searchFocusedColumn}
          onClose={() => { setSearchDrawerOpen(false); setSearchFocusedColumn(null); }}
        />
      )}

      {pickDialog && (
        <PickDialog
          action={pickDialog.action}
          row={pickDialog.row}
          onCancel={() => setPickDialog(null)}
          onConfirm={(value) => {
            rowActionMut.mutate({
              action: pickDialog.action,
              row: pickDialog.row,
              extraBody: { [pickDialog.action.pick!.bodyKey]: value },
            });
          }}
          pending={rowActionMut.isPending}
        />
      )}

      {bulkEditOpen && (
        <BulkEditDialog
          config={config}
          ids={[...selected]}
          isGranted={isGranted}
          isPending={bulkEditMut.isPending}
          onClose={() => setBulkEditOpen(false)}
          onSubmit={(patch) => bulkEditMut.mutate({ ids: [...selected], patch })}
        />
      )}

      {customDialog && customDialog.action.customDialog && (() => {
        const Component = customDialog.action.customDialog;
        return (
          <Component
            row={customDialog.row}
            onClose={() => setCustomDialog(null)}
            onComplete={() => {
              setCustomDialog(null);
              qc.invalidateQueries({ queryKey: [config.endpoint] });
            }}
          />
        );
      })()}

      {toolbarDialog && toolbarDialog.customDialog && (() => {
        const Component = toolbarDialog.customDialog;
        return (
          <Component
            onClose={() => setToolbarDialog(null)}
            onComplete={() => {
              setToolbarDialog(null);
              qc.invalidateQueries({ queryKey: [config.endpoint] });
            }}
          />
        );
      })()}

      {viewing && (
        <DetailDialog
          config={config}
          row={viewing}
          onClose={() => setViewing(null)}
          onEdit={canEditRow(viewing) ? () => openEdit(viewing) : undefined}
          onDelete={canDeleteRow(viewing)
            ? () => confirmAndDelete(viewing, () => setViewing(null))
            : undefined}
          rowActions={hasRowActions
            ? config.rowActions!.filter((a) => isGranted(a.actionKey)).filter((a) => !a.visible || a.visible(viewing))
            : undefined}
          onTriggerRowAction={(action) => {
            triggerRowAction(action, viewing);
          }}
          pending={rowActionMut.isPending}
          renderRelatedLists={viewing.id && config.relatedLists?.length
            ? () => (
              <>
                {config.relatedLists!.map((rl) => (
                  <RelatedList key={rl.endpoint + (rl.filterExtra ? JSON.stringify(rl.filterExtra) : '')} config={rl} parentId={viewing.id} />
                ))}
              </>
            )
            : undefined}
        />
      )}

      {ctxMenu && (() => {
        const row = ctxMenu.row;
        const visibleRowActions = hasRowActions
          ? config.rowActions!.filter((a) => isGranted(a.actionKey)).filter((a) => !a.visible || a.visible(row))
          : [];
        const items = buildRowContextItems({
          row,
          onView: config.clickRowToView !== false ? () => openDetail(row) : undefined,
          onEdit: canEditRow(row) ? () => openEdit(row) : undefined,
          onDelete: canDeleteRow(row)
            ? () => confirmAndDelete(row)
            : undefined,
          rowActions: visibleRowActions,
          onTriggerRowAction: (a) => triggerRowAction(a, row),
        });
        return <RowContextMenu x={ctxMenu.x} y={ctxMenu.y} items={items} onClose={() => setCtxMenu(null)} />;
      })()}
    </div>
  );
}

// ===== Built-in bulk-edit dialog =====
// Shows the same fields as the single-record Edit dialog, but each one is
// paired with an "Apply" checkbox. Only checked fields are sent in the
// PATCH /:prefix/bulk body — every other field on the selected records is
// left untouched. The disabled overlay mirrors the visual the admin-only
// fields use elsewhere so it reads as "filled in but not part of the
// submission yet".
function BulkEditDialog({ config, ids, isGranted, isPending, onClose, onSubmit }: {
  config: CrudConfig;
  ids: string[];
  isGranted: (key?: string) => boolean;
  isPending: boolean;
  onClose: () => void;
  onSubmit: (patch: Record<string, any>) => void;
}) {
  const [enabled, setEnabled] = React.useState<Record<string, boolean>>({});
  const [values, setValues] = React.useState<Record<string, any>>({});

  // Skip field types that don't make sense to bulk-set (legacy uploads, the
  // address bundle that needs map picking, the per-record sub-forms). Admin-
  // only fields stay hidden for non-admins to mirror the single-record form.
  const fields = config.fields.filter((f) => {
    if (f.type === 'image' || f.type === 'file') return false;
    if (f.type === 'address' || f.type === 'address-search') return false;
    if (f.type === 'custom') return false;
    if (f.unlockAction && !isGranted(f.unlockAction)) return false;
    return true;
  });

  const patch: Record<string, any> = {};
  for (const f of fields) {
    if (enabled[f.name]) patch[f.name] = values[f.name] ?? null;
  }
  const applyCount = Object.keys(patch).length;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Bulk edit {ids.length} {config.singular}{ids.length === 1 ? '' : 's'}</DialogTitle>
          <p className="text-xs text-muted-foreground">
            Tick the fields you want to update. Only ticked fields are written; every other column on the selected records is left as-is.
          </p>
        </DialogHeader>
        <div className="space-y-2 overflow-y-auto pr-1 flex-1 min-h-0">
          {fields.map((f) => {
            const on = !!enabled[f.name];
            return (
              <div key={f.name} className="flex items-start gap-3 p-2 rounded border">
                <input
                  type="checkbox"
                  checked={on}
                  onChange={(e) => setEnabled((prev) => ({ ...prev, [f.name]: e.target.checked }))}
                  className="mt-2 shrink-0"
                  aria-label={`Apply ${f.label}`}
                />
                <div className="flex-1 min-w-0 space-y-1">
                  <Label className={cn(!on && 'text-muted-foreground')}>{f.label}</Label>
                  <div className={cn(!on && 'opacity-40 pointer-events-none')}>
                    <Field
                      f={f}
                      value={values[f.name]}
                      onChange={(v) => setValues((prev) => ({ ...prev, [f.name]: v }))}
                      formValues={values}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <DialogFooter className="border-t pt-3">
          <Button variant="outline" onClick={onClose} disabled={isPending}>Cancel</Button>
          <Button
            onClick={() => onSubmit(patch)}
            disabled={isPending || applyCount === 0}
          >
            {isPending ? 'Applying…' : `Apply ${applyCount} field${applyCount === 1 ? '' : 's'} to ${ids.length} record${ids.length === 1 ? '' : 's'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ===== Pick-a-ref dialog used by rowActions with `pick:` =====
function PickDialog({
  action, row, onCancel, onConfirm, pending,
}: {
  action: RowActionConfig;
  row: any;
  onCancel: () => void;
  onConfirm: (value: string | string[] | null) => void;
  pending: boolean;
}) {
  const pick = action.pick!;
  const [single, setSingle] = React.useState<string | null>(null);
  const [multi, setMulti] = React.useState<string[]>(() => pick.multi ? refsToIds(get(row, pick.bodyKey === 'employeeIds' ? 'assignedTo' : pick.bodyKey)) : []);
  const { data } = useQuery({
    queryKey: ['pick', pick.endpoint],
    queryFn: async () => (await api<any[]>(`${pick.endpoint}?limit=200`)).data || [],
  });
  const items = data || [];
  const ready = pick.multi ? multi.length >= 0 : !!single;
  function toggle(id: string) {
    setMulti((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  }
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{pick.title || action.label}</DialogTitle>
        </DialogHeader>
        <div className="py-2 space-y-1.5">
          <Label>{pick.multi ? 'Select one or more' : 'Select a value'}</Label>
          {pick.multi ? (
            <div className="max-h-72 overflow-y-auto rounded-md border p-2 space-y-1">
              {items.map((it: any) => (
                <label key={it.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={multi.includes(it.id)} onCheckedChange={() => toggle(it.id)} />
                  <span>{get(it, pick.refLabel) || it.name || it.id}</span>
                </label>
              ))}
              {items.length === 0 && <div className="text-xs text-muted-foreground">No options</div>}
            </div>
          ) : (
            <Select value={single ?? ''} onValueChange={(v) => setSingle(v)}>
              <SelectTrigger><SelectValue placeholder="— Select —" /></SelectTrigger>
              <SelectContent>
                {items.map((it: any) => (
                  <SelectItem key={it.id} value={it.id}>
                    {get(it, pick.refLabel) || it.name || it.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <p className="text-xs text-muted-foreground pt-2">
            Applied to: <span className="font-medium">{row?.jobSheetNumber || row?.name || row?.id}</span>
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={() => onConfirm(pick.multi ? multi : single)} disabled={!ready || pending}>
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ===== Related list component =====
function RelatedList({ config, parentId }: { config: RelatedListConfig; parentId: string }) {
  const qc = useQueryClient();
  const queryKey = ['related', config.endpoint, config.filterField, parentId];
  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      const params = new URLSearchParams({
        [config.filterField]: parentId,
        limit: '100',
        ...(config.filterExtra ?? {}),
      });
      const r = await api<any[]>(`${config.endpoint}?${params.toString()}`);
      return { items: r.data || [], total: r.meta?.total ?? 0 };
    },
    enabled: !!parentId && !config.inlineEditor,
  });

  // Inline edit dialog state — populated when a row is clicked.
  const [editingRow, setEditingRow] = React.useState<any | null>(null);
  const [rowFormValues, setRowFormValues] = React.useState<Record<string, any>>({});

  // Distinguish create vs edit on the same row dialog. `creating` is true
  // when the user clicked "+ Add"; the dialog opens with empty values and
  // posts to the endpoint with `filterField: parentId` auto-applied.
  const [creating, setCreating] = React.useState(false);

  const updateRow = useMutation({
    mutationFn: async (body: any) => {
      if (!editingRow?.id) throw new Error('Missing row id');
      const r = await api(`${config.endpoint}/${editingRow.id}`, { method: 'PUT', body: JSON.stringify(body) });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
      return r.data;
    },
    onSuccess: () => {
      toast(`${config.rowEditSingular || 'Item'} updated`, undefined, 'success');
      qc.invalidateQueries({ queryKey });
      setEditingRow(null);
      setCreating(false);
    },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  const createRow = useMutation({
    mutationFn: async (body: any) => {
      const r = await api(config.endpoint, { method: 'POST', body: JSON.stringify(body) });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
      return r.data;
    },
    onSuccess: () => {
      toast(`${config.rowEditSingular || 'Item'} added`, undefined, 'success');
      qc.invalidateQueries({ queryKey });
      setEditingRow(null);
      setCreating(false);
    },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  function openRowEdit(row: any) {
    if (!config.rowEditFields) return;
    setCreating(false);
    setEditingRow(row);
    setRowFormValues(row);
  }
  function openRowCreate() {
    if (!config.rowEditFields) return;
    setCreating(true);
    // Sentinel row triggers the dialog; real id is assigned post-save.
    setEditingRow({ __new: true });
    // Seed the parent ref so any rowEditField with `dependsOn` on the parent
    // (e.g. multi-ref schedule picker filtered by servicePlan) resolves
    // immediately on open instead of waiting for the user to set it.
    setRowFormValues({
      ...(config.rowCreateDefaults ?? {}),
      [config.filterField]: parentId,
    });
  }

  function submitRowEdit() {
    let body: any = {};
    for (const f of config.rowEditFields ?? []) {
      const v = get(rowFormValues, f.name);
      if (v !== undefined && v !== '') body = set(body, f.name, v);
    }
    for (const f of config.rowEditFields ?? []) {
      if (f.type === 'ref') {
        const id = refToId(get(body, f.name));
        if (id != null) body = set(body, f.name, id);
      } else if (f.type === 'multi-ref') {
        body = set(body, f.name, refsToIds(get(body, f.name)));
      }
    }
    if (creating) {
      // Auto-stamp the parent reference + any static defaults the config
      // declared. Without this, the new row has no link to the parent
      // record and disappears from the related list immediately.
      body[config.filterField] = parentId;
      for (const [k, v] of Object.entries(config.rowCreateDefaults ?? {})) {
        if (body[k] === undefined) body[k] = v;
      }
      createRow.mutate(body);
    } else {
      updateRow.mutate(body);
    }
  }

  if (config.inlineEditor === 'service-checklist') {
    return (
      <div className="border-t pt-4">
        <h3 className="text-sm font-semibold mb-2">{config.title}</h3>
        <JobCardChecklist jobCardId={parentId} />
      </div>
    );
  }
  if (config.inlineEditor === 'onboarding-tasks') {
    return (
      <div className="border-t pt-4">
        <h3 className="text-sm font-semibold mb-2">{config.title}</h3>
        <OnboardingTasks onboardAssetId={parentId} />
      </div>
    );
  }
  if (config.inlineEditor === 'transfer-tasks') {
    return (
      <div className="border-t pt-4">
        <h3 className="text-sm font-semibold mb-2">{config.title}</h3>
        <TransferTasks assetTransferId={parentId} />
      </div>
    );
  }

  // Collapse long related-list tables. Keeps the detail dialog skim-able
  // when a vehicle has hundreds of dispatch jobs or service records. User
  // can opt in to "Show all" per section.
  const COLLAPSED_LIMIT = 5;
  const [expanded, setExpanded] = React.useState(false);
  // Sort a copy — the query cache hands back the same array reference on every
  // render, and sorting it in place would mutate cached data.
  const allItems = React.useMemo(() => {
    const items = data?.items ?? [];
    return config.sortBy ? [...items].sort(config.sortBy) : items;
  }, [data?.items, config.sortBy]);
  const visibleItems = expanded ? allItems : allItems.slice(0, COLLAPSED_LIMIT);
  const hiddenCount = Math.max(0, allItems.length - COLLAPSED_LIMIT);

  return (
    <div className="border-t pt-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold">{config.title} {data?.total != null && <span className="text-muted-foreground font-normal">({data.total})</span>}</h3>
        {config.rowCreate && config.rowEditFields && (
          <Button size="sm" variant="outline" onClick={openRowCreate}>
            <Plus className="h-3.5 w-3.5 mr-1" /> Add
          </Button>
        )}
      </div>
      {isLoading ? (
        <div className="text-xs text-muted-foreground py-3">Loading related records…</div>
      ) : !allItems.length ? (
        <div className="text-xs text-muted-foreground py-3">No related records.</div>
      ) : (
        <>
          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  {config.columns.map((c) => <TableHead key={c.key}>{c.label}</TableHead>)}
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleItems.map((row: any) => {
                  const rowClickable = !!config.rowEditFields && !config.disableRowClickEdit;
                  return (
                  <TableRow
                    key={row.id}
                    className={rowClickable ? 'cursor-pointer hover:bg-accent/40' : ''}
                    onClick={rowClickable ? () => openRowEdit(row) : undefined}
                  >
                    {config.columns.map((c) => (
                      <TableCell key={c.key} className="text-sm">{c.render ? c.render(row) : (get(row, c.key) ?? '—')}</TableCell>
                    ))}
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          {hiddenCount > 0 && (
            <div className="mt-2">
              <Button variant="ghost" size="sm" onClick={() => setExpanded((x) => !x)}>
                {expanded ? 'Show less' : `Show ${hiddenCount} more`}
              </Button>
            </div>
          )}
        </>
      )}

      {editingRow && config.rowEditFields && (
        <Dialog open onOpenChange={(o) => { if (!o) setEditingRow(null); }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{creating ? 'Add' : 'Edit'} {config.rowEditSingular || 'Item'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
              {config.rowEditFields.map((f) => (
                <div key={f.name} className={cn(f.type === 'boolean' ? '' : 'space-y-1.5')}>
                  {f.type !== 'boolean' && <Label>{f.label}{f.required && <span className="text-destructive"> *</span>}</Label>}
                  <Field
                    f={f}
                    value={get(rowFormValues, f.name)}
                    onChange={(v) => setRowFormValues((p) => set({ ...p }, f.name, v))}
                    formValues={rowFormValues}
                  />
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingRow(null)}>Cancel</Button>
              <Button onClick={submitRowEdit} disabled={updateRow.isPending || createRow.isPending}>
                {creating ? (createRow.isPending ? 'Adding…' : 'Add') : (updateRow.isPending ? 'Updating…' : 'Update')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ===== Column resize handle =====
//
// Thin vertical strip on the right edge of a header cell. Mousedown captures
// the parent <TableHead>'s current width, then mousemove emits the delta until
// mouseup. The CrudPage clamps to a 60px minimum and persists via
// UserPreference (namespace `crud-widths:${endpoint}`) so widths follow the
// user across devices.

function ResizeHandle({ onResize }: { onResize: (deltaPx: number, startWidth: number) => void }) {
  function onMouseDown(e: React.MouseEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    const handle = e.currentTarget;
    const head = handle.parentElement as HTMLElement | null;
    if (!head) return;
    const startX = e.clientX;
    const startWidth = head.getBoundingClientRect().width;
    const onMove = (ev: MouseEvent) => onResize(ev.clientX - startX, startWidth);
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }
  return (
    <div
      onMouseDown={onMouseDown}
      className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40 active:bg-primary/60"
      // Prevent the dropdown trigger button (which fills the header) from
      // intercepting the pointerdown that starts the drag.
      onClick={(e) => e.stopPropagation()}
      title="Drag to resize"
    />
  );
}

